// Typed client over the ac-edge tenant API (served same-origin at /api/*).
//
// Every call attaches `Authorization: Bearer <token>` from the session store. A
// 401 clears the session and throws ApiError(401) so the router bounces to Login.
// Endpoints + payload shapes mirror the `tenant` Hono app in
// `workers/ac-edge/src/index.ts` exactly.

import { clearSession, getToken } from "./auth";
import { saveBlob } from "./download";
import type {
  AddCommentInput,
  AssigneesResponse,
  RoleVisibilityResponse,
  WorkTypeId,
  PeopleFieldId,
  ClientsResponse,
  AttachmentRow,
  AuditResponse,
  AutomationsResponse,
  BacklogResponse,
  BoardColumnRow,
  BoardResponse,
  BrandingResponse,
  WorkspaceConfigResponse,
  SetWorkspaceConfigInput,
  EmailIntakeConfigResponse,
  SetEmailIntakeConfigInput,
  BurndownResponse,
  BusinessHoursResponse,
  CannedResponsesResponse,
  CommentResponse,
  ConfigCannedCreateResponse,
  ConfigCannedResponse,
  CreateCannedInput,
  CreatePriorityInput,
  CreateProjectInput,
  CreateSlaPolicyInput,
  CreateStatusInput,
  CreateTaskInput,
  CreateTicketFromTaskInput,
  CreateTicketInput,
  CustomFieldsResponse,
  DashboardRenderResponse,
  DashboardsResponse,
  EscalateInput,
  FiltersResponse,
  FormsResponse,
  AuthUser,
  LoginResponse,
  MeResponse,
  MoveTaskInput,
  MyWorkResponse,
  AccessRequest,
  AccessRequestResponse,
  MyAccessRequestsResponse,
  PendingAccessRequestsResponse,
  ProjectMembersResponse,
  ProjectRole,
  ProjectVisibility,
  TenantUser,
  TenantUsersResponse,
  RolesResponse,
  PermissionsResponse,
  NotificationsResponse,
  PrioritiesResponse,
  PriorityResponse,
  TaskPrioritiesResponse,
  TaskPriorityResponse,
  TicketTypesResponse,
  TicketTypeResponse,
  TaskStatusesResponse,
  TaskStatusResponse,
  ProjectResponse,
  ProjectRow,
  ProjectsResponse,
  CreateViewInput,
  ViewResponse,
  TimelineResponse,
  CalendarResponse,
  TableResponse,
  ReportResult,
  ReportRow,
  ReportsResponse,
  ReportSchedule,
  ReportSchedulesResponse,
  SearchResponse,
  SetBrandingInput,
  SetBusinessHoursInput,
  SlaPoliciesResponse,
  SlaPolicyResponse,
  SlaTimersResponse,
  SprintsResponse,
  SsoBeginResponse,
  SsoConnectionsResponse,
  SsoConnectionsAdminResponse,
  SsoConnectionAdminRow,
  CreateSsoConnectionInput,
  DirectoryConnection,
  DirectoryConnectionsResponse,
  CreateEntraConnectionInput,
  DirectorySyncSummary,
  DirectorySyncRun,
  StatusResponse,
  StatusesResponse,
  TaskCommentsResponse,
  TaskDetailResponse,
  TaskResponse,
  ActionItemsResponse,
  ActionItemSharesResponse,
  CreateActionItemInput,
  TaskTagsResponse,
  TasksResponse,
  TemplatesResponse,
  // Academic terms + rollover (higher-ed):
  CreateTermInput,
  RolloverProjectInput,
  TermResponse,
  TermsResponse,
  UpdateProjectInput,
  TicketDetailResponse,
  ActivityResponse,
  TicketResponse,
  TicketTemplatesResponse,
  TicketTemplateResponse,
  TicketsResponse,
  TicketDuplicatesResponse,
  TicketTimeResponse,
  ImportTicketRow,
  ImportTicketsResponse,
  UpdateTaskInput,
  UpdateTicketInput,
  VelocityResponse,
  WorkloadResponse,
} from "./types";
import type {
  CreateDashboardResponse,
  CreateDashboardWidgetInput,
  CreateDashboardWidgetResponse,
  DashboardWidgetRow,
} from "./types";
// Departments (higher-ed; 0021) + teams (the routing targets):
import type {
  CreateDepartmentInput,
  DepartmentApproversResponse,
  DepartmentResponse,
  DepartmentsResponse,
  ItSystem,
  ItSystemsResponse,
  TeamRow,
  TeamsResponse,
  UserTeamsResponse,
  AssetStatus,
  AssetsResponse,
  AssetResponse,
  CheckoutPeriod,
  AssetHoldersResponse,
  AssignmentHistoryResponse,
  AssetDetailResponse,
  TicketAssetsResponse,
  AssetTicketsResponse,
  AssetTypeRow,
  AssetTypesResponse,
  AssetCategoryRow,
  AssetCategoriesResponse,
  AssetStatusesResponse,
  AssetStatusResponse,
  CreateAssetInput,
  UpdateAssetInput,
  AssetImportRow,
  AssetImportResult,
} from "./types";
// Sprint mutations + Goals / OKRs + portfolios:
import type {
  CompleteSprintResponse,
  CreateGoalInput,
  CreateSprintInput,
  GoalResponse,
  GoalRollupResponse,
  GoalsResponse,
  PortfolioResponse,
  PortfoliosResponse,
  SprintResponse,
  UpdateGoalInput,
} from "./types";
// Facilities module (building-management request queue):
import type {
  ChangeApprover,
  ChangeApproversResponse,
  ChangeConfig,
  ChangeRequestCommentResponse,
  ChangeRequestDetailResponse,
  ChangeRequestFilter,
  ChangeRequestResponse,
  ChangeRequestsResponse,
  CreateChangeRequestInput,
  UpdateChangeRequestInput,
  CreateFacilityAssetInput,
  CreateFacilityPartInput,
  CreateFacilityRequestInput,
  FacilityGroupRow,
  FacilityGroupsResponse,
  FacilityGroupMembersResponse,
  FacilityGroupMessageRow,
  FacilityGroupMessagesResponse,
  FacilityRequestComment,
  FacilityVendorsResponse,
  FacilityVendorResponse,
  CreateFacilityVendorInput,
  UpdateFacilityVendorInput,
  FacilityAssetVendorsResponse,
  FacilityAssetDetailResponse,
  FacilityAssetDowntimeResponse,
  FacilityAssetFilter,
  FacilityAssetResponse,
  FacilityAssetWorkOrdersResponse,
  FacilityAssetsResponse,
  FacilityDowntimeMutationResponse,
  FacilityChecklistItemResponse,
  FacilityChecklistResponse,
  FacilityTasksResponse,
  FacilityTaskResponse,
  FacilityCommentResponse,
  FacilityPartFilter,
  FacilityPartResponse,
  FacilityPartsResponse,
  FacilityRequestDetailResponse,
  FacilityRequestFilter,
  FacilityPmTemplateResponse,
  FacilityPmTemplatesResponse,
  FacilityPmGenerateResponse,
  CreateFacilityPmTemplateInput,
  UpdateFacilityPmTemplateInput,
  FacilityRequestPartResponse,
  FacilityRequestPartsResponse,
  FacilityRequestResponse,
  FacilityRequestsResponse,
  UpdateFacilityAssetInput,
  UpdateFacilityPartInput,
  UpdateFacilityRequestInput,
} from "./types";
import type {
  TicketFlowField,
  TicketFlowStep,
  TicketFlowsResponse,
  TicketFlowResponse,
  TicketFlowRunResponse,
  TicketFlowRunsResponse,
  TicketFlowRunDetailResponse,
  PublicTicketFlow,
} from "./types";

/** Same-origin by default ("") — the SPA is served on the tenant host and the
 *  ac-edge Worker answers /api/* on the same origin. Override only for local dev. */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

/** Staging/dev only: the app and edge live on different hosts (pages.dev vs
 *  workers.dev), so the shared edge host can't resolve a tenant from KV. When set,
 *  every request carries X-AC-Tenant-Host so the (staging) edge targets the tenant
 *  by its real host. Unset in production, where the host already is the tenant. */
const TENANT_HOST = (import.meta.env.VITE_TENANT_HOST ?? "").trim();

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Explicit token (used by Login before it is persisted). */
  token?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = opts.token ?? getToken() ?? "";

  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? "GET",
      headers,
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, `Network error reaching the API${API_BASE ? ` (${API_BASE})` : ""}.`);
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    // On 401, drop the (now-invalid) session; the router redirects to Login.
    if (res.status === 401) clearSession();
    const message =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : undefined) ?? `Request failed (${res.status} ${res.statusText})`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ===== Auth ================================================================

export function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>("/api/auth/login", { method: "POST", body: { email, password } });
}

/** Validate a token against the authenticated probe (used at login + on boot). */
export function getMe(token?: string, signal?: AbortSignal): Promise<MeResponse> {
  return request<MeResponse>("/api/auth/me", {
    ...(token ? { token } : {}),
    ...(signal ? { signal } : {}),
  });
}

/** Update the signed-in user's own profile (currently the photo URL; blank clears it). */
export function updateMyProfile(input: { avatar_url: string | null }): Promise<{ user: AuthUser | null }> {
  return request<{ user: AuthUser | null }>("/api/auth/me", { method: "PATCH", body: input });
}

export function logout(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/auth/logout", { method: "POST" });
}

/** Enabled SSO connections for the Login page (no secrets exposed). */
export function listSsoConnections(signal?: AbortSignal): Promise<SsoConnectionsResponse> {
  return request<SsoConnectionsResponse>("/api/auth/sso/connections", {
    ...(signal ? { signal } : {}),
  });
}

/** Begin an SSO login: the response carries the IdP authorizationUrl to redirect to. */
export function beginSso(id: string): Promise<SsoBeginResponse> {
  return request<SsoBeginResponse>(`/api/auth/sso/${encodeURIComponent(id)}/begin`, {
    method: "POST",
  });
}

// ===== SSO sign-in connection management (Settings → Identity, admin) =======

/** Admin list of the tenant's SSO sign-in connections (client secret stripped). */
export function listSsoConnectionsAdmin(signal?: AbortSignal): Promise<SsoConnectionsAdminResponse> {
  return request<SsoConnectionsAdminResponse>("/api/sso/connections", { ...(signal ? { signal } : {}) });
}
/** Create an OIDC sign-in connection (Microsoft derives Entra endpoints from directory_id). */
export function createSsoConnection(input: CreateSsoConnectionInput): Promise<{ connection: SsoConnectionAdminRow }> {
  return request<{ connection: SsoConnectionAdminRow }>("/api/sso/connections", { method: "POST", body: input });
}
export function updateSsoConnection(
  id: string,
  patch: Partial<CreateSsoConnectionInput> & { enabled?: boolean },
): Promise<{ connection: SsoConnectionAdminRow }> {
  return request<{ connection: SsoConnectionAdminRow }>(`/api/sso/connections/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}
export function deleteSsoConnection(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/sso/connections/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ===== Directory (Azure Entra ID) — admin only =============================

/** List the tenant's directory connections (client secret never returned). */
export function listDirectoryConnections(
  signal?: AbortSignal,
): Promise<DirectoryConnectionsResponse> {
  return request<DirectoryConnectionsResponse>("/api/directory/connections", {
    ...(signal ? { signal } : {}),
  });
}

/** Create/connect an Azure Entra ID directory (encrypts the client secret). */
export function createEntraConnection(
  input: CreateEntraConnectionInput,
): Promise<{ connection: DirectoryConnection }> {
  return request<{ connection: DirectoryConnection }>("/api/directory/entra", {
    method: "POST",
    body: input,
  });
}

/** Trigger an on-demand sync for a connection (pulls/provisions users now). */
export function syncDirectoryConnection(id: string): Promise<DirectorySyncSummary> {
  return request<DirectorySyncSummary>(
    `/api/directory/connections/${encodeURIComponent(id)}/sync`,
    { method: "POST" },
  );
}

/** Recent sync runs for a connection (history of pulls). */
export function listDirectoryRuns(
  id: string,
  signal?: AbortSignal,
): Promise<{ runs: DirectorySyncRun[] }> {
  return request<{ runs: DirectorySyncRun[] }>(
    `/api/directory/connections/${encodeURIComponent(id)}/runs`,
    { ...(signal ? { signal } : {}) },
  );
}

// ===== Tickets =============================================================

export function listTickets(
  filters: {
    status?: string;
    priority?: string;
    assignee?: string;
    project?: string;
    q?: string;
    department?: string;
    /** "My Group": restrict to the caller's teams (resolved server-side). */
    mineTeam?: boolean;
  } = {},
  signal?: AbortSignal,
): Promise<TicketsResponse> {
  return request<TicketsResponse>(
    `/api/tickets${qs({
      status: filters.status,
      priority: filters.priority,
      assignee: filters.assignee,
      project: filters.project,
      q: filters.q,
      department: filters.department,
      ...(filters.mineTeam ? { mine_team: "1" } : {}),
    })}`,
    { ...(signal ? { signal } : {}) },
  );
}

export function createTicket(input: CreateTicketInput): Promise<TicketResponse> {
  return request<TicketResponse>("/api/tickets", { method: "POST", body: input });
}

// ---- Ticket templates (0108): reusable ticket settings ----
export function listTicketTemplates(signal?: AbortSignal): Promise<TicketTemplatesResponse> {
  return request<TicketTemplatesResponse>("/api/ticket-templates", { ...(signal ? { signal } : {}) });
}

/** Snapshot a ticket's reusable settings (not assignee/comments) into a template. */
export function createTicketTemplate(input: {
  name: string;
  is_shared: boolean;
  from_ticket_id: string;
}): Promise<TicketTemplateResponse> {
  return request<TicketTemplateResponse>("/api/ticket-templates", { method: "POST", body: input });
}

/** Rename a template or toggle its sharing (owner/admin only). */
export function updateTicketTemplate(
  id: string,
  patch: { name?: string; is_shared?: boolean },
): Promise<TicketTemplateResponse> {
  return request<TicketTemplateResponse>(`/api/ticket-templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteTicketTemplate(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/ticket-templates/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ---- Ticket flows (0109): intake forms that auto-create tickets + send email ----
export function listTicketFlows(signal?: AbortSignal): Promise<TicketFlowsResponse> {
  return request<TicketFlowsResponse>("/api/ticket-flows", { ...(signal ? { signal } : {}) });
}
export function getTicketFlow(id: string, signal?: AbortSignal): Promise<TicketFlowResponse> {
  return request<TicketFlowResponse>(`/api/ticket-flows/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}
export function createTicketFlow(body: {
  name: string;
  description?: string | null;
  intake_form: TicketFlowField[];
  steps: TicketFlowStep[];
  public_slug?: string | null;
  public_enabled?: number;
  brand_logo_url?: string | null;
  brand_color?: string | null;
}): Promise<TicketFlowResponse> {
  return request<TicketFlowResponse>("/api/ticket-flows", { method: "POST", body });
}
export function updateTicketFlow(
  id: string,
  patch: {
    name?: string;
    description?: string | null;
    is_active?: number;
    intake_form?: TicketFlowField[];
    steps?: TicketFlowStep[];
    public_slug?: string | null;
    public_enabled?: number;
    brand_logo_url?: string | null;
    brand_color?: string | null;
  },
): Promise<TicketFlowResponse> {
  return request<TicketFlowResponse>(`/api/ticket-flows/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}
export function deleteTicketFlow(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/ticket-flows/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function initiateTicketFlow(
  id: string,
  form_data: Record<string, unknown>,
): Promise<TicketFlowRunResponse> {
  return request<TicketFlowRunResponse>(`/api/ticket-flows/${encodeURIComponent(id)}/initiate`, {
    method: "POST",
    body: { form_data },
  });
}
export function listTicketFlowRuns(id: string, signal?: AbortSignal): Promise<TicketFlowRunsResponse> {
  return request<TicketFlowRunsResponse>(`/api/ticket-flows/${encodeURIComponent(id)}/runs`, {
    ...(signal ? { signal } : {}),
  });
}
export function getTicketFlowRun(id: string, signal?: AbortSignal): Promise<TicketFlowRunDetailResponse> {
  return request<TicketFlowRunDetailResponse>(`/api/ticket-flow-runs/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

// Public flow intake (0110) — anonymous, used by the /flow/:slug share page.
export function getPublicTicketFlow(slug: string, signal?: AbortSignal): Promise<{ flow: PublicTicketFlow }> {
  return request<{ flow: PublicTicketFlow }>(`/api/flow/${encodeURIComponent(slug)}`, {
    ...(signal ? { signal } : {}),
  });
}
export function initiatePublicTicketFlow(
  slug: string,
  body: { form_data: Record<string, unknown>; requester: { name?: string; email: string } },
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/flow/${encodeURIComponent(slug)}/initiate`, {
    method: "POST",
    body,
  });
}

/** Bulk-import historical tickets migrated from a previous system. Send up to
 *  500 normalized rows per call; the server creates each independently and
 *  reports per-row outcomes. */
export function importTickets(rows: ImportTicketRow[]): Promise<ImportTicketsResponse> {
  return request<ImportTicketsResponse>("/api/tickets/import", { method: "POST", body: { rows } });
}

/** Roster of active users for assignment pickers (technician dropdown).
 *  Optionally scoped by a (workType, field) context so role-visibility settings
 *  can restrict which users appear in that specific picker. No ctx = all users. */
export function listAssignees(
  signal?: AbortSignal,
  ctx?: { workType?: WorkTypeId; field?: PeopleFieldId },
): Promise<AssigneesResponse> {
  const query = qs({ work_type: ctx?.workType, field: ctx?.field });
  return request<AssigneesResponse>(`/api/assignees${query}`, { ...(signal ? { signal } : {}) });
}

/** Prior clients + departments for the New Ticket intake autocomplete. */
export function listClients(signal?: AbortSignal): Promise<ClientsResponse> {
  return request<ClientsResponse>("/api/clients", { ...(signal ? { signal } : {}) });
}

/** Upload a file to a ticket. Sends the raw bytes with x-filename (the attach
 *  endpoint reads the body as an ArrayBuffer), authenticated like every call. */
export async function uploadTicketAttachment(
  ticketId: string,
  file: File,
): Promise<{ attachment: AttachmentRow }> {
  const token = getToken() ?? "";
  const headers: Record<string, string> = {
    // Header values must be ISO-8859-1; non-ASCII filename chars would make
    // fetch() throw, so replace them (the edge sanitizes again server-side).
    "x-filename": file.name.replace(/[^\x20-\x7E]/g, "_") || "upload.bin",
    "Content-Type": file.type || "application/octet-stream",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;
  const res = await fetch(`${API_BASE}/api/tickets/${encodeURIComponent(ticketId)}/attachments`, {
    method: "POST",
    headers,
    body: await file.arrayBuffer(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || `Upload failed (${res.status})`);
  }
  return (await res.json()) as { attachment: AttachmentRow };
}

export function getTicket(id: string, signal?: AbortSignal): Promise<TicketDetailResponse> {
  return request<TicketDetailResponse>(`/api/tickets/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

/** Per-ticket activity timeline (who did what, when). Staff-only on the server. */
export function listTicketActivity(id: string, signal?: AbortSignal): Promise<ActivityResponse> {
  return request<ActivityResponse>(`/api/tickets/${encodeURIComponent(id)}/activity`, {
    ...(signal ? { signal } : {}),
  });
}

// ---- AI assists for a ticket (Workers AI). Staff-only on the server. -------
export function summarizeTicket(id: string): Promise<{ summary: string }> {
  return request<{ summary: string }>(`/api/tickets/${encodeURIComponent(id)}/ai/summarize`, { method: "POST" });
}
export interface AiReplySuggestion {
  reply: string;
  sources: { id: string; title: string; slug: string }[];
}
export function suggestTicketReply(id: string): Promise<AiReplySuggestion> {
  return request<AiReplySuggestion>(`/api/tickets/${encodeURIComponent(id)}/ai/suggest-reply`, { method: "POST" });
}
export interface AiTriageSuggestion {
  suggestion: { priority_code: string | null; category: string | null; reason: string };
}
export function triageTicket(id: string): Promise<AiTriageSuggestion> {
  return request<AiTriageSuggestion>(`/api/tickets/${encodeURIComponent(id)}/ai/triage`, { method: "POST" });
}

// 0097: AI duplicate-detection.
export function analyzeTicketDuplicates(id: string): Promise<TicketDuplicatesResponse> {
  return request<TicketDuplicatesResponse>(`/api/tickets/${encodeURIComponent(id)}/ai/duplicates`, { method: "POST" });
}
export function listTicketDuplicates(id: string, signal?: AbortSignal): Promise<TicketDuplicatesResponse> {
  return request<TicketDuplicatesResponse>(`/api/tickets/${encodeURIComponent(id)}/duplicates`, {
    ...(signal ? { signal } : {}),
  });
}
export function setTicketDuplicateStatus(
  id: string,
  linkId: string,
  status: "dismissed" | "confirmed",
): Promise<{ duplicate: import("./types").TicketDuplicate }> {
  return request(`/api/tickets/${encodeURIComponent(id)}/duplicates/${encodeURIComponent(linkId)}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export function updateTicket(id: string, patch: UpdateTicketInput): Promise<TicketResponse> {
  return request<TicketResponse>(`/api/tickets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

/** Replace the full tag set on a ticket (PUT /api/tickets/:id/tags). */
export function setTicketTags(id: string, tags: string[]): Promise<TaskTagsResponse> {
  return request<TaskTagsResponse>(`/api/tickets/${encodeURIComponent(id)}/tags`, {
    method: "PUT",
    body: { tags },
  });
}

export type TicketLinkRelation = "related" | "duplicates" | "caused_by";

/** Link this ticket to another ticket (ticket↔ticket relation). */
export function linkTicket(id: string, targetId: string, relation: TicketLinkRelation): Promise<unknown> {
  return request<unknown>(`/api/tickets/${encodeURIComponent(id)}/links`, {
    method: "POST",
    body: { target_id: targetId, relation },
  });
}

/** Remove a ticket↔ticket link. */
export function unlinkTicket(id: string, targetId: string, relation: TicketLinkRelation): Promise<unknown> {
  return request<unknown>(`/api/tickets/${encodeURIComponent(id)}/links`, {
    method: "DELETE",
    body: { target_id: targetId, relation },
  });
}

// ----- Ticket hours-worked log (0032) -----
export function listTicketTime(id: string, signal?: AbortSignal): Promise<TicketTimeResponse> {
  return request<TicketTimeResponse>(`/api/tickets/${encodeURIComponent(id)}/time`, { ...(signal ? { signal } : {}) });
}
export function logTicketTime(id: string, body: { minutes: number; note?: string | null }): Promise<TicketTimeResponse> {
  return request<TicketTimeResponse>(`/api/tickets/${encodeURIComponent(id)}/time`, { method: "POST", body });
}
export function deleteTicketTime(id: string, logId: string): Promise<TicketTimeResponse> {
  return request<TicketTimeResponse>(`/api/tickets/${encodeURIComponent(id)}/time/${encodeURIComponent(logId)}`, { method: "DELETE" });
}

export function addComment(id: string, input: AddCommentInput): Promise<CommentResponse> {
  return request<CommentResponse>(`/api/tickets/${encodeURIComponent(id)}/comments`, {
    method: "POST",
    body: input,
  });
}

/** Edit a comment's body (author or staff). */
export function updateComment(commentId: string, body: string): Promise<CommentResponse> {
  return request<CommentResponse>(`/api/comments/${encodeURIComponent(commentId)}`, {
    method: "PATCH",
    body: { body },
  });
}

/** Delete a comment (author or staff). */
export function deleteComment(commentId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/comments/${encodeURIComponent(commentId)}`, {
    method: "DELETE",
  });
}

export function escalateTicket(id: string, input: EscalateInput): Promise<TaskResponse> {
  return request<TaskResponse>(`/api/tickets/${encodeURIComponent(id)}/escalate`, {
    method: "POST",
    body: input,
  });
}

export function listCannedResponses(signal?: AbortSignal): Promise<CannedResponsesResponse> {
  return request<CannedResponsesResponse>("/api/canned-responses", { ...(signal ? { signal } : {}) });
}

/** Build a same-origin download URL for an attachment (Bearer is not sent on a
 *  plain link; the endpoint streams from R2 — acceptable for this slice). */
export function attachmentUrl(id: string): string {
  return `${API_BASE}/api/attachments/${encodeURIComponent(id)}`;
}

/** Fetch an attachment's bytes WITH auth and return an object URL the browser
 *  can render in <img> (the download endpoint requires a Bearer that a plain
 *  <img src> can't send). The caller MUST URL.revokeObjectURL when done. */
export async function attachmentObjectUrl(id: string): Promise<string> {
  const token = getToken() ?? "";
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;
  const res = await fetch(`${API_BASE}/api/attachments/${encodeURIComponent(id)}`, { headers });
  if (!res.ok) throw new ApiError(res.status, `Failed to load attachment (${res.status})`);
  return URL.createObjectURL(await res.blob());
}

// ===== Projects / boards / tasks ===========================================

export function listProjects(
  archived = false,
  signal?: AbortSignal,
  term?: string,
): Promise<ProjectsResponse> {
  return request<ProjectsResponse>(
    `/api/projects${qs({ ...(archived ? { archived: "1" } : {}), term })}`,
    { ...(signal ? { signal } : {}) },
  );
}

export function createProject(input: CreateProjectInput): Promise<ProjectResponse> {
  return request<ProjectResponse>("/api/projects", { method: "POST", body: input });
}

export function getProject(id: string, signal?: AbortSignal): Promise<ProjectResponse> {
  return request<ProjectResponse>(`/api/projects/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

/** Edit a project's own fields (PATCH /api/projects/:id). Send only changed
 *  fields; null clears a nullable column. Requires project "manager" access. */
export function updateProject(id: string, patch: UpdateProjectInput): Promise<{ project: ProjectRow }> {
  return request<{ project: ProjectRow }>(`/api/projects/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

/** Term rollover: clone a project (boards, views, open tasks) into a new term. */
export function rolloverProject(id: string, input: RolloverProjectInput): Promise<ProjectResponse> {
  return request<ProjectResponse>(`/api/projects/${encodeURIComponent(id)}/rollover`, {
    method: "POST",
    body: input,
  });
}

// ===== Saved views (List / Board / Timeline / Calendar / Table / Workload) ===

/** Create a new saved view of any supported type for a project. */
export function createView(projectId: string, input: CreateViewInput): Promise<ViewResponse> {
  return request<ViewResponse>(`/api/projects/${encodeURIComponent(projectId)}/views`, {
    method: "POST",
    body: input,
  });
}

export function deleteView(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/views/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ===== Work-management deletes =============================================

export function deleteProject(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function deleteTask(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/tasks/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function deleteBoard(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/boards/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function deleteTicket(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/tickets/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function deleteEpic(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/epics/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function deleteMilestone(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/milestones/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function deleteSprint(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/sprints/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ===== Deletes for the remaining managed items ============================
export function deleteGoal(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/goals/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deletePortfolio(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/portfolios/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deleteDashboard(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/dashboards/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deleteWidget(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/widgets/${encodeURIComponent(id)}`, { method: "DELETE" });
}
/** Update a widget (title / query / layout). `layout_json` carries the width
 *  (`{ w: 1..4 }` columns) among any other layout state. */
export function updateWidget(
  id: string,
  patch: { title?: string | null; query_json?: unknown; layout_json?: unknown },
): Promise<{ widget: DashboardWidgetRow }> {
  return request<{ widget: DashboardWidgetRow }>(`/api/widgets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}
export function deleteForm(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/forms/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deleteFilter(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/filters/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deleteTemplate(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/templates/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deleteTeam(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/teams/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deleteCustomField(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/custom-fields/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function deleteTicketCategory(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/ticket-categories/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function getProjectTimeline(projectId: string, signal?: AbortSignal): Promise<TimelineResponse> {
  return request<TimelineResponse>(`/api/projects/${encodeURIComponent(projectId)}/timeline`, {
    ...(signal ? { signal } : {}),
  });
}

export function getProjectCalendar(projectId: string, signal?: AbortSignal): Promise<CalendarResponse> {
  return request<CalendarResponse>(`/api/projects/${encodeURIComponent(projectId)}/calendar`, {
    ...(signal ? { signal } : {}),
  });
}

export function getProjectTable(projectId: string, signal?: AbortSignal): Promise<TableResponse> {
  return request<TableResponse>(`/api/projects/${encodeURIComponent(projectId)}/table`, {
    ...(signal ? { signal } : {}),
  });
}

export function listProjectTasks(
  id: string,
  filters: { priority_id?: string; department?: string } = {},
  signal?: AbortSignal,
): Promise<TasksResponse> {
  return request<TasksResponse>(
    `/api/projects/${encodeURIComponent(id)}/tasks${qs({
      priority_id: filters.priority_id,
      department: filters.department,
    })}`,
    { ...(signal ? { signal } : {}) },
  );
}

export function getBoard(id: string, signal?: AbortSignal): Promise<BoardResponse> {
  return request<BoardResponse>(`/api/boards/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

// ----- Board columns = a project's custom task statuses (CRUD) -----
export function addBoardColumn(boardId: string, body: { name: string; wip_limit?: number | null; color?: string | null }): Promise<{ column: BoardColumnRow }> {
  return request<{ column: BoardColumnRow }>(`/api/boards/${encodeURIComponent(boardId)}/columns`, { method: "POST", body });
}
export function updateBoardColumn(
  boardId: string,
  colId: string,
  patch: { name?: string; wip_limit?: number | null; color?: string | null },
): Promise<{ column: BoardColumnRow }> {
  return request<{ column: BoardColumnRow }>(
    `/api/boards/${encodeURIComponent(boardId)}/columns/${encodeURIComponent(colId)}`,
    { method: "PATCH", body: patch },
  );
}
export function deleteBoardColumn(boardId: string, colId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/boards/${encodeURIComponent(boardId)}/columns/${encodeURIComponent(colId)}`,
    { method: "DELETE" },
  );
}
export function reorderBoardColumns(boardId: string, orderedIds: string[]): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/boards/${encodeURIComponent(boardId)}/columns/reorder`, {
    method: "POST",
    body: { ordered_ids: orderedIds },
  });
}

export function createTask(input: CreateTaskInput): Promise<TaskResponse> {
  return request<TaskResponse>("/api/tasks", { method: "POST", body: input });
}

/** Escalate a task into a helpdesk ticket (inverse of escalateTicket). */
export function createTicketFromTask(
  id: string,
  input?: CreateTicketFromTaskInput,
): Promise<TicketResponse> {
  return request<TicketResponse>(`/api/tasks/${encodeURIComponent(id)}/escalate-to-ticket`, {
    method: "POST",
    body: input ?? {},
  });
}

export function getTask(id: string, signal?: AbortSignal): Promise<TaskDetailResponse> {
  return request<TaskDetailResponse>(`/api/tasks/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function updateTask(id: string, patch: UpdateTaskInput): Promise<TaskResponse> {
  return request<TaskResponse>(`/api/tasks/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

/** Task discussion thread. */
export function listTaskComments(id: string, signal?: AbortSignal): Promise<TaskCommentsResponse> {
  return request<TaskCommentsResponse>(`/api/tasks/${encodeURIComponent(id)}/comments`, {
    ...(signal ? { signal } : {}),
  });
}
export function addTaskComment(
  id: string,
  body: string,
  opts?: { notify_teams?: boolean; mentions?: string[] },
): Promise<{ id: string }> {
  return request<{ id: string }>(`/api/tasks/${encodeURIComponent(id)}/comments`, {
    method: "POST",
    body: {
      body,
      ...(opts?.notify_teams ? { notify_teams: true } : {}),
      ...(opts?.mentions && opts.mentions.length ? { mentions: opts.mentions } : {}),
    },
  });
}
/** Toggle an emoji reaction on a task comment (0034); returns the refreshed thread. */
export function toggleTaskCommentReaction(
  taskId: string,
  commentId: string,
  emoji: string,
): Promise<TaskCommentsResponse> {
  return request<TaskCommentsResponse>(
    `/api/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}/react`,
    { method: "POST", body: { emoji } },
  );
}

export function moveTask(id: string, input: MoveTaskInput): Promise<TaskResponse> {
  return request<TaskResponse>(`/api/tasks/${encodeURIComponent(id)}/move`, {
    method: "POST",
    body: input,
  });
}

// ----- Task tags (mirrors the ticket tag routes) -----
// GET reads the current tags; PUT replaces the whole set; each returns the list.

export function getTaskTags(id: string, signal?: AbortSignal): Promise<TaskTagsResponse> {
  return request<TaskTagsResponse>(`/api/tasks/${encodeURIComponent(id)}/tags`, {
    ...(signal ? { signal } : {}),
  });
}

export function setTaskTags(id: string, tags: string[]): Promise<TaskTagsResponse> {
  return request<TaskTagsResponse>(`/api/tasks/${encodeURIComponent(id)}/tags`, {
    method: "PUT",
    body: { tags },
  });
}

/** WebSocket URL for the live board room (graceful — callers wrap in try/catch).
 *  WebSockets can't send custom headers, so on staging (where the edge can't read
 *  the tenant from its own host) we pass the tenant host as a query param instead;
 *  the edge honors it only when ALLOW_HOST_OVERRIDE is on. */
export function boardSocketUrl(boardId: string): string {
  const base = API_BASE || window.location.origin;
  const url = new URL(`/api/boards/${encodeURIComponent(boardId)}/socket`, base);
  if (TENANT_HOST) url.searchParams.set("__tenant_host", TENANT_HOST);
  // WebSockets can't send an Authorization header — pass the session token as a
  // query param; the edge accepts it for upgrade requests only (see authContext).
  const token = getToken();
  if (token) url.searchParams.set("access_token", token);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

// ===== Agile ===============================================================

export function getBacklog(projectId: string, signal?: AbortSignal): Promise<BacklogResponse> {
  return request<BacklogResponse>(`/api/projects/${encodeURIComponent(projectId)}/backlog`, {
    ...(signal ? { signal } : {}),
  });
}

export function listSprints(boardId: string, signal?: AbortSignal): Promise<SprintsResponse> {
  return request<SprintsResponse>(`/api/boards/${encodeURIComponent(boardId)}/sprints`, {
    ...(signal ? { signal } : {}),
  });
}

export function getBurndown(sprintId: string, signal?: AbortSignal): Promise<BurndownResponse> {
  return request<BurndownResponse>(`/api/sprints/${encodeURIComponent(sprintId)}/burndown`, {
    ...(signal ? { signal } : {}),
  });
}

export function getVelocity(boardId: string, signal?: AbortSignal): Promise<VelocityResponse> {
  return request<VelocityResponse>(`/api/boards/${encodeURIComponent(boardId)}/velocity`, {
    ...(signal ? { signal } : {}),
  });
}

export function getWorkload(projectId: string, signal?: AbortSignal): Promise<WorkloadResponse> {
  return request<WorkloadResponse>(`/api/projects/${encodeURIComponent(projectId)}/workload`, {
    ...(signal ? { signal } : {}),
  });
}

// ===== Dashboards ==========================================================

export function listDashboards(signal?: AbortSignal): Promise<DashboardsResponse> {
  return request<DashboardsResponse>("/api/dashboards", { ...(signal ? { signal } : {}) });
}

export function renderDashboard(id: string, signal?: AbortSignal): Promise<DashboardRenderResponse> {
  return request<DashboardRenderResponse>(`/api/dashboards/${encodeURIComponent(id)}/render`, {
    ...(signal ? { signal } : {}),
  });
}

/** Rename a dashboard. */
export function updateDashboard(id: string, name: string): Promise<CreateDashboardResponse> {
  return request<CreateDashboardResponse>(`/api/dashboards/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { name },
  });
}

// ===== My Work =============================================================

// Identity comes from the session server-side; no user id is sent.
export function getMyWork(signal?: AbortSignal): Promise<MyWorkResponse> {
  return request<MyWorkResponse>("/api/my-work", { ...(signal ? { signal } : {}) });
}

/** Self-service: open a ticket as yourself into the intake ("Pre Ticket") lane.
 *  Helpdesk then assigns it and moves it to Open. */
export function requestTicket(input: { subject: string; description?: string }): Promise<TicketResponse> {
  return request<TicketResponse>("/api/tickets/request", { method: "POST", body: input });
}

// ===== Action Items (standalone tasks) =====================================

/** Action Items visible to the caller (created by or assigned to them). */
export function listActionItems(signal?: AbortSignal): Promise<ActionItemsResponse> {
  return request<ActionItemsResponse>("/api/action-items", { ...(signal ? { signal } : {}) });
}

/** Create a standalone Action Item. Detail/edit reuse the task endpoints
 *  (getTask/updateTask/comments/subtasks) via the returned task id. */
export function createActionItem(input: CreateActionItemInput): Promise<TaskResponse> {
  return request<TaskResponse>("/api/action-items", { method: "POST", body: input });
}

// ----- 0090 read-only sharing (edit-access only) ---------------------------

/** Current read-only-share roster for an action item (requires edit access). */
export function listActionItemShares(taskId: string, signal?: AbortSignal): Promise<ActionItemSharesResponse> {
  return request<ActionItemSharesResponse>(`/api/action-items/${encodeURIComponent(taskId)}/shares`, {
    ...(signal ? { signal } : {}),
  });
}

/** Add tenant users as read-only viewers (idempotent). Returns refreshed roster. */
export function addActionItemShares(taskId: string, user_ids: string[]): Promise<ActionItemSharesResponse> {
  return request<ActionItemSharesResponse>(`/api/action-items/${encodeURIComponent(taskId)}/shares`, {
    method: "POST",
    body: { user_ids },
  });
}

/** Remove a user's read-only share. Returns refreshed roster. */
export function removeActionItemShare(taskId: string, userId: string): Promise<ActionItemSharesResponse> {
  return request<ActionItemSharesResponse>(
    `/api/action-items/${encodeURIComponent(taskId)}/shares/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

// ===== Project access control (members / visibility / requests) ============

export function listProjectMembers(projectId: string, signal?: AbortSignal): Promise<ProjectMembersResponse> {
  return request<ProjectMembersResponse>(`/api/projects/${encodeURIComponent(projectId)}/members`, {
    ...(signal ? { signal } : {}),
  });
}

export function addProjectMember(
  projectId: string,
  input: { user_id: string; role?: ProjectRole },
): Promise<ProjectMembersResponse> {
  return request<ProjectMembersResponse>(`/api/projects/${encodeURIComponent(projectId)}/members`, {
    method: "POST",
    body: input,
  });
}

export function updateProjectMember(
  projectId: string,
  userId: string,
  role: ProjectRole,
): Promise<ProjectMembersResponse> {
  return request<ProjectMembersResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { method: "PATCH", body: { role } },
  );
}

export function removeProjectMember(projectId: string, userId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export function setProjectVisibility(
  projectId: string,
  visibility: ProjectVisibility,
): Promise<{ ok: boolean; visibility: ProjectVisibility }> {
  return request(`/api/projects/${encodeURIComponent(projectId)}/visibility`, {
    method: "PUT",
    body: { visibility },
  });
}

export function requestProjectAccess(
  projectId: string,
  input: { requested_role?: ProjectRole; note?: string } = {},
): Promise<AccessRequestResponse> {
  return request<AccessRequestResponse>(`/api/projects/${encodeURIComponent(projectId)}/access-requests`, {
    method: "POST",
    body: input,
  });
}

export function listPendingAccessRequests(signal?: AbortSignal): Promise<PendingAccessRequestsResponse> {
  return request<PendingAccessRequestsResponse>("/api/access-requests/pending", {
    ...(signal ? { signal } : {}),
  });
}

export function listMyAccessRequests(signal?: AbortSignal): Promise<MyAccessRequestsResponse> {
  return request<MyAccessRequestsResponse>("/api/access-requests/mine", {
    ...(signal ? { signal } : {}),
  });
}

export function approveAccessRequest(id: string): Promise<{ request: AccessRequest }> {
  return request(`/api/access-requests/${encodeURIComponent(id)}/approve`, { method: "POST" });
}

export function denyAccessRequest(id: string): Promise<{ request: AccessRequest }> {
  return request(`/api/access-requests/${encodeURIComponent(id)}/deny`, { method: "POST" });
}

/** Tenant user roster (admin-gated) — for admin pickers. */
export function listTenantUsers(signal?: AbortSignal): Promise<TenantUsersResponse> {
  return request<TenantUsersResponse>("/api/users", { ...(signal ? { signal } : {}) });
}

// ===== Settings (read-only loads for this slice) ===========================

export function listCustomFields(
  entity: "task" | "ticket" = "task",
  signal?: AbortSignal,
): Promise<CustomFieldsResponse> {
  return request<CustomFieldsResponse>(`/api/custom-fields${qs({ entity })}`, {
    ...(signal ? { signal } : {}),
  });
}

export function listAutomations(signal?: AbortSignal): Promise<AutomationsResponse> {
  return request<AutomationsResponse>("/api/automations", { ...(signal ? { signal } : {}) });
}

export function listForms(signal?: AbortSignal): Promise<FormsResponse> {
  return request<FormsResponse>("/api/forms", { ...(signal ? { signal } : {}) });
}

export function listTemplates(signal?: AbortSignal): Promise<TemplatesResponse> {
  return request<TemplatesResponse>("/api/templates", { ...(signal ? { signal } : {}) });
}

export function listFilters(signal?: AbortSignal): Promise<FiltersResponse> {
  return request<FiltersResponse>("/api/filters", { ...(signal ? { signal } : {}) });
}

// ===== Notifications =======================================================

// Identity is taken from the session (Bearer token); the server ignores any
// user id, so we don't send one (mirrors getMyWork).
export function listNotifications(signal?: AbortSignal): Promise<NotificationsResponse> {
  return request<NotificationsResponse>("/api/notifications", {
    ...(signal ? { signal } : {}),
  });
}

export function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/notifications/${encodeURIComponent(id)}/read`, {
    method: "POST",
  });
}

export function markAllNotificationsRead(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/notifications/read-all", { method: "POST" });
}

// ===== Global search =======================================================

export function search(q: string, signal?: AbortSignal): Promise<SearchResponse> {
  return request<SearchResponse>(`/api/search${qs({ q })}`, { ...(signal ? { signal } : {}) });
}

// ===== Reports =============================================================

export function listReports(signal?: AbortSignal): Promise<ReportsResponse> {
  return request<ReportsResponse>("/api/reports", { ...(signal ? { signal } : {}) });
}

export function runReport(id: string, signal?: AbortSignal): Promise<ReportResult> {
  return request<ReportResult>(`/api/reports/${encodeURIComponent(id)}/run`, {
    ...(signal ? { signal } : {}),
  });
}

/** Ad-hoc run: execute a kind + config without saving (builder preview, or a
 *  saved report re-run with a different date window). */
export function runReportConfig(
  body: { kind: string; config?: Record<string, unknown> },
  signal?: AbortSignal,
): Promise<ReportResult> {
  return request<ReportResult>("/api/reports/run", { method: "POST", body, ...(signal ? { signal } : {}) });
}

export function createReport(input: {
  name: string;
  kind: string;
  module?: "helpdesk" | "work" | "combined";
  description?: string;
  config?: Record<string, unknown>;
}): Promise<{ report: ReportRow }> {
  return request<{ report: ReportRow }>("/api/reports", { method: "POST", body: input });
}

export function updateReport(
  id: string,
  patch: { name?: string; description?: string | null; module?: "helpdesk" | "work" | "combined"; kind?: string; config?: Record<string, unknown> },
): Promise<{ report: ReportRow }> {
  return request<{ report: ReportRow }>(`/api/reports/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteReport(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/reports/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Persist a manual ordering of the saved-reports list (full id order). */
export function reorderReports(ids: string[]): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/reports/order", { method: "PUT", body: { ids } });
}

// ----- Scheduled report delivery -----
export function listReportSchedules(reportId: string, signal?: AbortSignal): Promise<ReportSchedulesResponse> {
  return request<ReportSchedulesResponse>(`/api/reports/${encodeURIComponent(reportId)}/schedules`, { ...(signal ? { signal } : {}) });
}
export function createReportSchedule(
  reportId: string,
  body: { cron: string; recipients: string[]; enabled?: boolean },
): Promise<{ schedule: ReportSchedule }> {
  return request<{ schedule: ReportSchedule }>(`/api/reports/${encodeURIComponent(reportId)}/schedules`, { method: "POST", body });
}
export function updateReportSchedule(
  id: string,
  patch: { cron?: string; recipients?: string[]; enabled?: boolean },
): Promise<{ schedule: ReportSchedule }> {
  return request<{ schedule: ReportSchedule }>(`/api/report-schedules/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}
export function deleteReportSchedule(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/report-schedules/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ===== Tenant config (statuses / priorities / canned / hours / branding) ===

export function listStatuses(signal?: AbortSignal): Promise<StatusesResponse> {
  return request<StatusesResponse>("/api/config/statuses", { ...(signal ? { signal } : {}) });
}

export function createStatus(input: CreateStatusInput): Promise<StatusResponse> {
  return request<StatusResponse>("/api/config/statuses", { method: "POST", body: input });
}

export function updateStatus(id: string, patch: Partial<CreateStatusInput>): Promise<StatusResponse> {
  return request<StatusResponse>(`/api/config/statuses/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteStatus(id: string, reassignTo?: string): Promise<{ ok: boolean }> {
  const qs = reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : "";
  return request<{ ok: boolean }>(`/api/config/statuses/${encodeURIComponent(id)}${qs}`, { method: "DELETE" });
}

export function setDefaultStatus(id: string): Promise<StatusResponse> {
  return request<StatusResponse>(`/api/config/statuses/${encodeURIComponent(id)}/default`, { method: "POST" });
}

/** Persist a new display order for ticket statuses (full id order). */
export function reorderStatuses(ids: string[]): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/config/statuses/order", { method: "PUT", body: { ids } });
}

// ----- Academic terms (higher-ed) -----

export function listTerms(signal?: AbortSignal): Promise<TermsResponse> {
  return request<TermsResponse>("/api/config/terms", { ...(signal ? { signal } : {}) });
}

export function createTerm(input: CreateTermInput): Promise<TermResponse> {
  return request<TermResponse>("/api/config/terms", { method: "POST", body: input });
}

export function updateTerm(id: string, patch: Partial<CreateTermInput>): Promise<TermResponse> {
  return request<TermResponse>(`/api/config/terms/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteTerm(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/terms/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function setCurrentTerm(id: string): Promise<TermResponse> {
  return request<TermResponse>(`/api/config/terms/${encodeURIComponent(id)}/current`, { method: "POST" });
}

export function listPriorities(signal?: AbortSignal): Promise<PrioritiesResponse> {
  return request<PrioritiesResponse>("/api/config/priorities", { ...(signal ? { signal } : {}) });
}

export function createPriority(input: CreatePriorityInput): Promise<PriorityResponse> {
  return request<PriorityResponse>("/api/config/priorities", { method: "POST", body: input });
}

export function updatePriority(id: string, patch: Partial<CreatePriorityInput>): Promise<PriorityResponse> {
  return request<PriorityResponse>(`/api/config/priorities/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deletePriority(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/priorities/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function setDefaultPriority(id: string): Promise<PriorityResponse> {
  return request<PriorityResponse>(`/api/config/priorities/${encodeURIComponent(id)}/default`, { method: "POST" });
}

// ----- Task / action-item priorities (0108): separate set from tickets -----
export function listTaskPriorities(signal?: AbortSignal): Promise<TaskPrioritiesResponse> {
  return request<TaskPrioritiesResponse>("/api/config/task-priorities", { ...(signal ? { signal } : {}) });
}
export function createTaskPriority(input: CreatePriorityInput): Promise<TaskPriorityResponse> {
  return request<TaskPriorityResponse>("/api/config/task-priorities", { method: "POST", body: input });
}
export function updateTaskPriority(id: string, patch: Partial<CreatePriorityInput>): Promise<TaskPriorityResponse> {
  return request<TaskPriorityResponse>(`/api/config/task-priorities/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}
export function deleteTaskPriority(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/task-priorities/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function setDefaultTaskPriority(id: string): Promise<TaskPriorityResponse> {
  return request<TaskPriorityResponse>(`/api/config/task-priorities/${encodeURIComponent(id)}/default`, { method: "POST" });
}

// ----- Ticket types (configurable; Service Request / Incident / Problem / …) -----
export function listTicketTypes(signal?: AbortSignal): Promise<TicketTypesResponse> {
  return request<TicketTypesResponse>("/api/config/ticket-types", { ...(signal ? { signal } : {}) });
}
export function createTicketType(input: { code: string; name: string; color?: string | null }): Promise<TicketTypeResponse> {
  return request<TicketTypeResponse>("/api/config/ticket-types", { method: "POST", body: input });
}
export function updateTicketType(
  id: string,
  patch: { code?: string; name?: string; position?: number; color?: string | null },
): Promise<TicketTypeResponse> {
  return request<TicketTypeResponse>(`/api/config/ticket-types/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}
export function deleteTicketType(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/ticket-types/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function setDefaultTicketType(id: string): Promise<TicketTypeResponse> {
  return request<TicketTypeResponse>(`/api/config/ticket-types/${encodeURIComponent(id)}/default`, { method: "POST" });
}

// ----- Task statuses (configurable; To do / In progress / Done / …) -----
export function listTaskStatuses(signal?: AbortSignal): Promise<TaskStatusesResponse> {
  return request<TaskStatusesResponse>("/api/config/task-statuses", { ...(signal ? { signal } : {}) });
}
export function createTaskStatus(input: { code: string; name: string; color?: string | null }): Promise<TaskStatusResponse> {
  return request<TaskStatusResponse>("/api/config/task-statuses", { method: "POST", body: input });
}
export function updateTaskStatus(
  id: string,
  patch: { code?: string; name?: string; position?: number; color?: string | null },
): Promise<TaskStatusResponse> {
  return request<TaskStatusResponse>(`/api/config/task-statuses/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}
export function deleteTaskStatus(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/task-statuses/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function setDefaultTaskStatus(id: string): Promise<TaskStatusResponse> {
  return request<TaskStatusResponse>(`/api/config/task-statuses/${encodeURIComponent(id)}/default`, { method: "POST" });
}
/** Persist a new display order for task statuses (full id order). */
export function reorderTaskStatuses(ids: string[]): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/config/task-statuses/order", { method: "PUT", body: { ids } });
}

export function listConfigCanned(signal?: AbortSignal): Promise<ConfigCannedResponse> {
  return request<ConfigCannedResponse>("/api/config/canned", { ...(signal ? { signal } : {}) });
}

export function createConfigCanned(input: CreateCannedInput): Promise<ConfigCannedCreateResponse> {
  return request<ConfigCannedCreateResponse>("/api/config/canned", { method: "POST", body: input });
}

export function updateConfigCanned(id: string, patch: Partial<CreateCannedInput>): Promise<ConfigCannedCreateResponse> {
  return request<ConfigCannedCreateResponse>(`/api/config/canned/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteConfigCanned(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/canned/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function getBusinessHours(signal?: AbortSignal): Promise<BusinessHoursResponse> {
  return request<BusinessHoursResponse>("/api/config/business-hours", {
    ...(signal ? { signal } : {}),
  });
}

export function setBusinessHours(input: SetBusinessHoursInput): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/config/business-hours", { method: "PUT", body: input });
}

export function getWorkspaceConfig(signal?: AbortSignal): Promise<WorkspaceConfigResponse> {
  return request<WorkspaceConfigResponse>("/api/config/workspace", { ...(signal ? { signal } : {}) });
}

export function setWorkspaceConfig(input: SetWorkspaceConfigInput): Promise<WorkspaceConfigResponse> {
  return request<WorkspaceConfigResponse>("/api/config/workspace", { method: "PUT", body: input });
}

/** Ticket "Submission Tracking" (source-channel) options. Read = any member. */
export function getTicketSources(signal?: AbortSignal): Promise<{ sources: FacilityOption[] }> {
  return request<{ sources: FacilityOption[] }>("/api/config/ticket-sources", { ...(signal ? { signal } : {}) });
}
export function setTicketSources(sources: FacilityOption[]): Promise<{ sources: FacilityOption[] }> {
  return request<{ sources: FacilityOption[] }>("/api/config/ticket-sources", { method: "PUT", body: { sources } });
}

export function getActionItemCategories(signal?: AbortSignal): Promise<{ categories: FacilityOption[] }> {
  return request<{ categories: FacilityOption[] }>("/api/config/action-item-categories", { ...(signal ? { signal } : {}) });
}
export function setActionItemCategories(categories: FacilityOption[]): Promise<{ categories: FacilityOption[] }> {
  return request<{ categories: FacilityOption[] }>("/api/config/action-item-categories", { method: "PUT", body: { categories } });
}

export function getEmailIntakeConfig(signal?: AbortSignal): Promise<EmailIntakeConfigResponse> {
  return request<EmailIntakeConfigResponse>("/api/config/email-intake", { ...(signal ? { signal } : {}) });
}

export function setEmailIntakeConfig(input: SetEmailIntakeConfigInput): Promise<EmailIntakeConfigResponse> {
  return request<EmailIntakeConfigResponse>("/api/config/email-intake", { method: "PUT", body: input });
}

export function getBranding(signal?: AbortSignal): Promise<BrandingResponse> {
  return request<BrandingResponse>("/api/config/branding", { ...(signal ? { signal } : {}) });
}

export function setBranding(input: SetBrandingInput): Promise<BrandingResponse> {
  return request<BrandingResponse>("/api/config/branding", { method: "PUT", body: input });
}

/** Upload/replace the tenant logo (raw image bytes). Returns the updated
 *  branding with logo_url pointing at the served R2 object. */
export async function uploadBrandingLogo(file: File): Promise<BrandingResponse> {
  const token = getToken() ?? "";
  const headers: Record<string, string> = {
    "Content-Type": file.type || "application/octet-stream",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;
  const res = await fetch(`${API_BASE}/api/config/branding/logo`, {
    method: "POST",
    headers,
    body: await file.arrayBuffer(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let msg = `Upload failed (${res.status})`;
    try {
      const j = JSON.parse(text) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* keep default */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as BrandingResponse;
}

// ===== SLA =================================================================

export function listSlaPolicies(signal?: AbortSignal): Promise<SlaPoliciesResponse> {
  return request<SlaPoliciesResponse>("/api/sla/policies", { ...(signal ? { signal } : {}) });
}

export function createSlaPolicy(input: CreateSlaPolicyInput): Promise<SlaPolicyResponse> {
  return request<SlaPolicyResponse>("/api/sla/policies", { method: "POST", body: input });
}

export function updateSlaPolicy(
  id: string,
  patch: Omit<Partial<CreateSlaPolicyInput>, "priority_id"> & { priority_id?: string | null; active?: boolean },
): Promise<SlaPolicyResponse> {
  return request<SlaPolicyResponse>(`/api/sla/policies/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteSlaPolicy(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/sla/policies/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function getTicketSla(ticketId: string, signal?: AbortSignal): Promise<SlaTimersResponse> {
  return request<SlaTimersResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/sla`, {
    ...(signal ? { signal } : {}),
  });
}

/** Apply a specific SLA policy to a ticket (agent override of the auto-match). */
export function setTicketSla(ticketId: string, policyId: string): Promise<SlaTimersResponse> {
  return request<SlaTimersResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/sla`, {
    method: "POST",
    body: { policy_id: policyId },
  });
}

/** Remove the SLA from a ticket entirely (clears all timers). */
export function removeTicketSla(ticketId: string): Promise<SlaTimersResponse> {
  return request<SlaTimersResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/sla`, {
    method: "DELETE",
  });
}

// ===== Audit ===============================================================

export function listAudit(
  filters: { entity_type?: string; entity_id?: string } = {},
  signal?: AbortSignal,
): Promise<AuditResponse> {
  return request<AuditResponse>(
    `/api/audit${qs({ entity_type: filters.entity_type, entity_id: filters.entity_id })}`,
    { ...(signal ? { signal } : {}) },
  );
}

// ===== Dashboard creation (companions to the Dashboards reads above) ========

/** Create a dashboard (POST /api/dashboards — body is just { name }). */
export function createDashboard(name: string): Promise<CreateDashboardResponse> {
  return request<CreateDashboardResponse>("/api/dashboards", {
    method: "POST",
    body: { name },
  });
}

/** Add a widget to a dashboard (POST /api/dashboards/:id/widgets). */
export function addDashboardWidget(
  dashboardId: string,
  input: CreateDashboardWidgetInput,
): Promise<CreateDashboardWidgetResponse> {
  return request<CreateDashboardWidgetResponse>(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/widgets`,
    { method: "POST", body: input },
  );
}

// ===== Departments (higher-ed; 0021) + teams ===============================

/** Teams (groups) — the department routing targets. GET /api/teams. */
export function listTeams(signal?: AbortSignal): Promise<TeamsResponse> {
  return request<TeamsResponse>("/api/teams", { ...(signal ? { signal } : {}) });
}

/** Create a tech group (team). */
export function createTeam(name: string): Promise<{ team: TeamRow }> {
  return request<{ team: TeamRow }>("/api/teams", { method: "POST", body: { name } });
}

/** Rename a tech group (team). */
export function updateTeam(id: string, name: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/teams/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { name },
  });
}

// ===== Assets / inventory (0036) ==========================================

export function listAssets(
  params?: { status?: AssetStatus; holder_id?: string; category_id?: string },
  signal?: AbortSignal,
): Promise<AssetsResponse> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.holder_id) q.set("holder_id", params.holder_id);
  if (params?.category_id) q.set("category_id", params.category_id);
  const qs = q.toString();
  return request<AssetsResponse>(`/api/assets${qs ? `?${qs}` : ""}`, { ...(signal ? { signal } : {}) });
}

export function getAsset(id: string, signal?: AbortSignal): Promise<AssetDetailResponse> {
  return request<AssetDetailResponse>(`/api/assets/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

// ---- Asset ↔ ticket links (many-to-many, 0094) ----
export function listTicketAssets(ticketId: string, signal?: AbortSignal): Promise<TicketAssetsResponse> {
  return request<TicketAssetsResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/assets`, {
    ...(signal ? { signal } : {}),
  });
}

export function linkTicketAsset(ticketId: string, assetId: string): Promise<TicketAssetsResponse> {
  return request<TicketAssetsResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/assets`, {
    method: "POST",
    body: { asset_id: assetId },
  });
}

export function unlinkTicketAsset(ticketId: string, assetId: string): Promise<TicketAssetsResponse> {
  return request<TicketAssetsResponse>(
    `/api/tickets/${encodeURIComponent(ticketId)}/assets/${encodeURIComponent(assetId)}`,
    { method: "DELETE" },
  );
}

export function listAssetTickets(assetId: string, signal?: AbortSignal): Promise<AssetTicketsResponse> {
  return request<AssetTicketsResponse>(`/api/assets/${encodeURIComponent(assetId)}/tickets`, {
    ...(signal ? { signal } : {}),
  });
}

export function createAsset(input: CreateAssetInput): Promise<AssetResponse> {
  return request<AssetResponse>("/api/assets", { method: "POST", body: input });
}

/** Bulk-import assets from parsed CSV rows. */
export function importAssets(rows: AssetImportRow[]): Promise<AssetImportResult> {
  return request<AssetImportResult>("/api/assets/import", { method: "POST", body: { rows } });
}

export function updateAsset(id: string, input: UpdateAssetInput): Promise<AssetResponse> {
  return request<AssetResponse>(`/api/assets/${encodeURIComponent(id)}`, { method: "PATCH", body: input });
}

export function deleteAsset(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/assets/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Check an asset out to a user (userId set) or back in (userId null). Optional
 *  due/reminder dates (unix sec) attach to the check-out period. */
export function assignAsset(
  id: string,
  userId: string | null,
  opts?: { note?: string | null; due_at?: number | null; reminder_at?: number | null },
): Promise<AssetResponse & { current_period: CheckoutPeriod | null }> {
  return request<AssetResponse & { current_period: CheckoutPeriod | null }>(
    `/api/assets/${encodeURIComponent(id)}/assign`,
    {
      method: "POST",
      body: {
        user_id: userId,
        ...(opts?.note ? { note: opts.note } : {}),
        ...(opts?.due_at != null ? { due_at: opts.due_at } : {}),
        ...(opts?.reminder_at != null ? { reminder_at: opts.reminder_at } : {}),
      },
    },
  );
}

/** Check out `quantity` units of an asset to a user (multi-holder; other holders
 *  untouched). Rejects with the server message when retired / over-available. */
export function checkoutAsset(
  id: string,
  input: { user_id: string; quantity?: number; note?: string | null; due_at?: number | null; reminder_at?: number | null },
): Promise<AssetHoldersResponse> {
  return request<AssetHoldersResponse>(`/api/assets/${encodeURIComponent(id)}/checkout`, {
    method: "POST",
    body: {
      user_id: input.user_id,
      ...(input.quantity != null ? { quantity: input.quantity } : {}),
      ...(input.note ? { note: input.note } : {}),
      ...(input.due_at != null ? { due_at: input.due_at } : {}),
      ...(input.reminder_at != null ? { reminder_at: input.reminder_at } : {}),
    },
  });
}

/** Check IN one open check-out (by assignment id), releasing its units. */
export function checkinAsset(id: string, assignmentId: string, note?: string | null): Promise<AssetHoldersResponse> {
  return request<AssetHoldersResponse>(`/api/assets/${encodeURIComponent(id)}/checkin`, {
    method: "POST",
    body: { assignment_id: assignmentId, ...(note ? { note } : {}) },
  });
}

/** Edit the dates/reminder/status on the current open check-out period (no new
 *  custody event). status='returned' performs a check-in. */
export function updateCheckoutDates(
  id: string,
  patch: { due_at?: number | null; reminder_at?: number | null; status?: "checked_out" | "returned" },
): Promise<AssetResponse & { current_period: CheckoutPeriod | null }> {
  return request<AssetResponse & { current_period: CheckoutPeriod | null }>(
    `/api/assets/${encodeURIComponent(id)}/checkout-dates`,
    { method: "PATCH", body: patch },
  );
}

/** Assets a user currently holds (their "in possession" list). */
export function listUserAssets(userId: string, signal?: AbortSignal): Promise<AssetsResponse> {
  return request<AssetsResponse>(`/api/users/${encodeURIComponent(userId)}/assets`, {
    ...(signal ? { signal } : {}),
  });
}

/** Roster-wide custody history (newest first) — who held what in the past. */
export function listAssignmentHistory(limit = 200, signal?: AbortSignal): Promise<AssignmentHistoryResponse> {
  return request<AssignmentHistoryResponse>(`/api/assignments/history?limit=${limit}`, {
    ...(signal ? { signal } : {}),
  });
}

// Asset types (configurable lookup).
export function listAssetTypes(signal?: AbortSignal): Promise<AssetTypesResponse> {
  return request<AssetTypesResponse>("/api/config/asset-types", { ...(signal ? { signal } : {}) });
}
export function createAssetType(name: string): Promise<{ asset_type: AssetTypeRow }> {
  return request<{ asset_type: AssetTypeRow }>("/api/config/asset-types", { method: "POST", body: { name } });
}
export function updateAssetType(id: string, name: string): Promise<{ asset_type: AssetTypeRow }> {
  return request<{ asset_type: AssetTypeRow }>(`/api/config/asset-types/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { name },
  });
}
export function deleteAssetType(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/asset-types/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ----- Configurable asset categories (Settings → Asset Categories; 0092) -----
export function listAssetCategories(signal?: AbortSignal): Promise<AssetCategoriesResponse> {
  return request<AssetCategoriesResponse>("/api/config/asset-categories", { ...(signal ? { signal } : {}) });
}
export function createAssetCategory(name: string): Promise<{ asset_category: AssetCategoryRow }> {
  return request<{ asset_category: AssetCategoryRow }>("/api/config/asset-categories", { method: "POST", body: { name } });
}
export function updateAssetCategory(id: string, name: string): Promise<{ asset_category: AssetCategoryRow }> {
  return request<{ asset_category: AssetCategoryRow }>(`/api/config/asset-categories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { name },
  });
}
export function deleteAssetCategory(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/asset-categories/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ----- Configurable asset statuses (Settings → Asset Statuses) -----
export function listAssetStatuses(signal?: AbortSignal): Promise<AssetStatusesResponse> {
  return request<AssetStatusesResponse>("/api/config/asset-statuses", { ...(signal ? { signal } : {}) });
}
export function createAssetStatus(input: { name: string; color?: string | null }): Promise<AssetStatusResponse> {
  return request<AssetStatusResponse>("/api/config/asset-statuses", { method: "POST", body: input });
}
export function updateAssetStatus(
  id: string,
  patch: { name?: string; color?: string | null; position?: number },
): Promise<AssetStatusResponse> {
  return request<AssetStatusResponse>(`/api/config/asset-statuses/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}
export function deleteAssetStatus(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/asset-statuses/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function reorderAssetStatuses(ids: string[]): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/config/asset-statuses/order", { method: "PUT", body: { ids } });
}

/** A user's team memberships + role (admin). */
export function listUserTeams(userId: string, signal?: AbortSignal): Promise<UserTeamsResponse> {
  return request<UserTeamsResponse>(`/api/users/${encodeURIComponent(userId)}/teams`, {
    ...(signal ? { signal } : {}),
  });
}

/** Add the user to a team / set their role (member|manager) — upsert. */
export function setTeamMember(teamId: string, userId: string, role: "member" | "manager"): Promise<unknown> {
  return request<unknown>(`/api/teams/${encodeURIComponent(teamId)}/members`, {
    method: "POST",
    body: { user_id: userId, role },
  });
}

/** Remove the user from a team. */
export function removeTeamMember(teamId: string, userId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export function listDepartments(signal?: AbortSignal): Promise<DepartmentsResponse> {
  return request<DepartmentsResponse>("/api/config/departments", { ...(signal ? { signal } : {}) });
}

export function createDepartment(input: CreateDepartmentInput): Promise<DepartmentResponse> {
  return request<DepartmentResponse>("/api/config/departments", { method: "POST", body: input });
}

export function updateDepartment(id: string, patch: Partial<CreateDepartmentInput>): Promise<DepartmentResponse> {
  return request<DepartmentResponse>(`/api/config/departments/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteDepartment(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/departments/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ----- Department Change-Management approvers (0099) — default approver set
// seeded onto a change request when it's filed under this department. -----
export function listDepartmentApprovers(id: string, signal?: AbortSignal): Promise<DepartmentApproversResponse> {
  return request<DepartmentApproversResponse>(`/api/config/departments/${encodeURIComponent(id)}/approvers`, {
    ...(signal ? { signal } : {}),
  });
}
export function setDepartmentApprovers(id: string, userIds: string[]): Promise<DepartmentApproversResponse> {
  return request<DepartmentApproversResponse>(`/api/config/departments/${encodeURIComponent(id)}/approvers`, {
    method: "PUT",
    body: { user_ids: userIds },
  });
}

// ===== IT Systems (Settings lookup + change-request multi-select; 0101) ======
export function listItSystems(opts?: { includeInactive?: boolean }, signal?: AbortSignal): Promise<ItSystemsResponse> {
  const q = opts?.includeInactive ? "?include_inactive=1" : "";
  return request<ItSystemsResponse>(`/api/config/it-systems${q}`, { ...(signal ? { signal } : {}) });
}
export function createItSystem(input: { name: string; position?: number }): Promise<{ it_system: ItSystem }> {
  return request<{ it_system: ItSystem }>("/api/config/it-systems", { method: "POST", body: input });
}
export function updateItSystem(
  id: string,
  patch: { name?: string; position?: number; active?: boolean | number },
): Promise<{ it_system: ItSystem }> {
  return request<{ it_system: ItSystem }>(`/api/config/it-systems/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}
export function deleteItSystem(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/it-systems/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ===== Sprint mutations (companions to the Agile reads above) ==============

export function createSprint(boardId: string, input: CreateSprintInput): Promise<SprintResponse> {
  return request<SprintResponse>(`/api/boards/${encodeURIComponent(boardId)}/sprints`, {
    method: "POST",
    body: input,
  });
}

/** Start a sprint (future → active); optional end_at stamps the end date. */
export function startSprint(id: string, endAt?: number): Promise<SprintResponse> {
  return request<SprintResponse>(`/api/sprints/${encodeURIComponent(id)}/start`, {
    method: "POST",
    body: endAt !== undefined ? { end_at: endAt } : {},
  });
}

/** Complete a sprint (active → closed); returns how many unfinished tasks moved back. */
export function completeSprint(id: string): Promise<CompleteSprintResponse> {
  return request<CompleteSprintResponse>(`/api/sprints/${encodeURIComponent(id)}/complete`, {
    method: "POST",
  });
}

export function addTaskToSprint(sprintId: string, taskId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/sprints/${encodeURIComponent(sprintId)}/tasks/${encodeURIComponent(taskId)}`,
    { method: "POST" },
  );
}

// ===== Goals / OKRs + Portfolios ===========================================

export function listGoals(signal?: AbortSignal): Promise<GoalsResponse> {
  return request<GoalsResponse>("/api/goals", { ...(signal ? { signal } : {}) });
}

export function createGoal(input: CreateGoalInput): Promise<GoalResponse> {
  return request<GoalResponse>("/api/goals", { method: "POST", body: input });
}

export function updateGoal(id: string, patch: UpdateGoalInput): Promise<GoalResponse> {
  return request<GoalResponse>(`/api/goals/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

/** Recompute a goal's progress from its linked work (POST /api/goals/:id/rollup). */
export function rollupGoal(id: string): Promise<GoalRollupResponse> {
  return request<GoalRollupResponse>(`/api/goals/${encodeURIComponent(id)}/rollup`, {
    method: "POST",
  });
}

export function listPortfolios(signal?: AbortSignal): Promise<PortfoliosResponse> {
  return request<PortfoliosResponse>("/api/portfolios", { ...(signal ? { signal } : {}) });
}

export function createPortfolio(input: { name: string; description?: string }): Promise<PortfolioResponse> {
  return request<PortfolioResponse>("/api/portfolios", { method: "POST", body: input });
}

export function addProjectToPortfolio(portfolioId: string, projectId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/portfolios/${encodeURIComponent(portfolioId)}/projects`, {
    method: "POST",
    body: { project_id: projectId },
  });
}

// ===== Forms builder + Automations admin ====================================
// Companions to listForms/deleteForm/listAutomations above. NOTE: the edge
// exposes NO publish route (forms-db.publishForm is unwired) and POST
// /api/forms does not accept portal_enabled, so the builder omits both
// toggles. Type-only import kept local so this block stays append-only.
import type {
  AddFormFieldInput,
  AddFormFieldResponse,
  UpdateFormFieldInput,
  AutomationRuleResponse,
  AutomationRulesResponse,
  CreateAutomationInput,
  UpdateAutomationInput,
  CreateFormInput,
  UpdateFormInput,
  CreateFormResponse,
  FormWithFieldsResponse,
  CsatConfig,
  CsatConfigResponse,
  CsatBucket,
  CsatPublic,
  CsatResponsesPage,
  FavoriteIds,
  FavoriteType,
  FormSubmissionsResponse,
  FormStatsResponse,
} from "./types";

export function createForm(input: CreateFormInput): Promise<CreateFormResponse> {
  return request<CreateFormResponse>("/api/forms", { method: "POST", body: input });
}

/** Update a form's config (name/slug/creates/portal/default status). */
export function updateForm(id: string, patch: UpdateFormInput): Promise<CreateFormResponse> {
  return request<CreateFormResponse>(`/api/forms/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

/** Publish (true) or unpublish (false) a form. */
export function publishForm(id: string, published: boolean): Promise<CreateFormResponse> {
  return request<CreateFormResponse>(`/api/forms/${encodeURIComponent(id)}/publish`, {
    method: "POST",
    body: { published },
  });
}

// ---- CSAT surveys (0112) ----
export function getFavorites(signal?: AbortSignal): Promise<FavoriteIds> {
  return request<FavoriteIds>("/api/favorites", { ...(signal ? { signal } : {}) });
}
export function addFavorite(entity_type: FavoriteType, entity_id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/favorites", { method: "POST", body: { entity_type, entity_id } });
}
export function removeFavorite(entity_type: FavoriteType, entity_id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/favorites/${entity_type}/${encodeURIComponent(entity_id)}`, {
    method: "DELETE",
  });
}

export function getCsatConfig(signal?: AbortSignal): Promise<CsatConfigResponse> {
  return request<CsatConfigResponse>("/api/config/csat", { ...(signal ? { signal } : {}) });
}
export function setCsatConfig(patch: Partial<CsatConfig>): Promise<CsatConfigResponse> {
  return request<CsatConfigResponse>("/api/config/csat", { method: "PUT", body: patch });
}
/** Paged, filterable history of answered surveys (Settings → Satisfaction → History).
 *  `summary: false` skips the period aggregate — for export chunks, which only
 *  need the rows. */
export function listCsatResponses(
  params: {
    bucket?: CsatBucket | null;
    from?: number | null;
    limit?: number;
    offset?: number;
    summary?: boolean;
  },
  signal?: AbortSignal,
): Promise<CsatResponsesPage> {
  const q = new URLSearchParams();
  if (params.bucket) q.set("bucket", params.bucket);
  if (params.from != null) q.set("from", String(params.from));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  if (params.summary === false) q.set("summary", "0");
  const qs = q.toString();
  return request<CsatResponsesPage>(`/api/csat-responses${qs ? `?${qs}` : ""}`, {
    ...(signal ? { signal } : {}),
  });
}
/** Public: load a survey by its token (anonymous /csat/:token page). */
export function getCsat(token: string, signal?: AbortSignal): Promise<CsatPublic> {
  return request<CsatPublic>(`/api/csat/${encodeURIComponent(token)}`, { ...(signal ? { signal } : {}) });
}
/** Public: submit a survey rating (one-time). */
export function submitCsat(
  token: string,
  body: { rating: number; comment?: string | null },
): Promise<{ ok: boolean; already_responded: boolean }> {
  return request(`/api/csat/${encodeURIComponent(token)}`, { method: "POST", body });
}

/** Form + its ordered fields (GET /api/forms/:slug). */
export function getFormBySlug(slug: string, signal?: AbortSignal): Promise<FormWithFieldsResponse> {
  return request<FormWithFieldsResponse>(`/api/forms/${encodeURIComponent(slug)}`, {
    ...(signal ? { signal } : {}),
  });
}

/** Public intake submission (anonymous on portal-enabled forms). The server
 *  validates required + visible fields and creates the ticket/task. */
export function submitForm(slug: string, payload: Record<string, unknown>): Promise<{ submission_id?: string }> {
  return request<{ submission_id?: string }>(`/api/forms/${encodeURIComponent(slug)}/submit`, {
    method: "POST",
    body: { payload },
  });
}

export function addFormField(formId: string, input: AddFormFieldInput): Promise<AddFormFieldResponse> {
  return request<AddFormFieldResponse>(`/api/forms/${encodeURIComponent(formId)}/fields`, {
    method: "POST",
    body: input,
  });
}

/** Configure an existing field (partial: label/key/kind/required/options/branching). */
export function updateFormField(
  formId: string,
  fieldId: string,
  patch: UpdateFormFieldInput,
): Promise<AddFormFieldResponse> {
  return request<AddFormFieldResponse>(
    `/api/forms/${encodeURIComponent(formId)}/fields/${encodeURIComponent(fieldId)}`,
    { method: "PATCH", body: patch },
  );
}

export function deleteFormField(formId: string, fieldId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/forms/${encodeURIComponent(formId)}/fields/${encodeURIComponent(fieldId)}`,
    { method: "DELETE" },
  );
}

/** Recorded submissions for a form (newest first). */
export function listFormSubmissions(formId: string, signal?: AbortSignal): Promise<FormSubmissionsResponse> {
  return request<FormSubmissionsResponse>(`/api/forms/${encodeURIComponent(formId)}/submissions`, {
    ...(signal ? { signal } : {}),
  });
}

/** Conversion analytics for a form: views, submissions, entity breakdown (#4). */
export function getFormStats(formId: string, signal?: AbortSignal): Promise<FormStatsResponse> {
  return request<FormStatsResponse>(`/api/forms/${encodeURIComponent(formId)}/stats`, {
    ...(signal ? { signal } : {}),
  });
}

// ---- Daily agent digest opt-in (#9). ----
export function getDigestSetting(signal?: AbortSignal): Promise<{ enabled: boolean }> {
  return request<{ enabled: boolean }>("/api/config/digest", { ...(signal ? { signal } : {}) });
}
export function setDigestSetting(enabled: boolean): Promise<{ enabled: boolean }> {
  return request<{ enabled: boolean }>("/api/config/digest", { method: "PUT", body: { enabled } });
}

/** Rules with decoded trigger/conditions/actions (GET /api/automations). */
export function listAutomationRules(signal?: AbortSignal): Promise<AutomationRulesResponse> {
  return request<AutomationRulesResponse>("/api/automations", { ...(signal ? { signal } : {}) });
}

export function createAutomation(input: CreateAutomationInput): Promise<AutomationRuleResponse> {
  return request<AutomationRuleResponse>("/api/automations", { method: "POST", body: input });
}

/** Partial update of a rule (PATCH /api/automations/:id). */
export function updateAutomation(
  id: string,
  patch: UpdateAutomationInput,
): Promise<AutomationRuleResponse> {
  return request<AutomationRuleResponse>(`/api/automations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function toggleAutomation(id: string, enabled: boolean): Promise<AutomationRuleResponse> {
  return request<AutomationRuleResponse>(`/api/automations/${encodeURIComponent(id)}/toggle`, {
    method: "POST",
    body: { enabled },
  });
}

export function deleteAutomation(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/automations/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ===== Knowledge base (authoring; /api/kb/*) ================================
// Article writes need ticket.write; category writes need settings.admin.
// Type-only import kept local so this block stays append-only.
import type {
  CreateKbArticleInput,
  KbArticleResponse,
  KbArticlesResponse,
  KbCategoriesResponse,
  KbCategoryResponse,
  KbChatMessage,
  KbChatResponse,
  KbChatsResponse,
  KbChatDetailResponse,
  KbSuggestResponse,
  UpdateKbArticleInput,
  TicketKbArticlesResponse,
  KbLinkedTicketsResponse,
  CreateKbFromTicketInput,
  CreateKbFromTicketResponse,
  SubmitKbReviewInput,
} from "./types";

/** Every article, drafts included, with category (agent authoring list). */
export function listKbArticles(signal?: AbortSignal): Promise<KbArticlesResponse> {
  return request<KbArticlesResponse>("/api/kb/articles", { ...(signal ? { signal } : {}) });
}

export function getKbArticle(id: string, signal?: AbortSignal): Promise<KbArticleResponse> {
  return request<KbArticleResponse>(`/api/kb/articles/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function createKbArticle(input: CreateKbArticleInput): Promise<KbArticleResponse> {
  return request<KbArticleResponse>("/api/kb/articles", { method: "POST", body: input });
}

export function updateKbArticle(id: string, patch: UpdateKbArticleInput): Promise<KbArticleResponse> {
  return request<KbArticleResponse>(`/api/kb/articles/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

/** Publish (true) or unpublish back to draft (false). */
export function publishKbArticle(id: string, published: boolean): Promise<KbArticleResponse> {
  return request<KbArticleResponse>(`/api/kb/articles/${encodeURIComponent(id)}/publish`, {
    method: "POST",
    body: { published },
  });
}

/** Submit a draft for publish review (0096); names an approver. */
export function submitKbArticleForReview(id: string, input: SubmitKbReviewInput): Promise<KbArticleResponse> {
  return request<KbArticleResponse>(`/api/kb/articles/${encodeURIComponent(id)}/submit-for-review`, {
    method: "POST",
    body: input,
  });
}

/** Approve (publishes) or reject (back to draft) a pending KB review (0096). */
export function decideKbApproval(id: string, input: DecideApprovalInput): Promise<KbArticleResponse> {
  return request<KbArticleResponse>(`/api/kb/approvals/${encodeURIComponent(id)}/decide`, {
    method: "POST",
    body: input,
  });
}

export function deleteKbArticle(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/kb/articles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function listKbCategories(signal?: AbortSignal): Promise<KbCategoriesResponse> {
  return request<KbCategoriesResponse>("/api/kb/categories", { ...(signal ? { signal } : {}) });
}

export function createKbCategory(input: { name: string; slug: string; position?: number }): Promise<KbCategoryResponse> {
  return request<KbCategoryResponse>("/api/kb/categories", { method: "POST", body: input });
}

export function updateKbCategory(
  id: string,
  patch: { name?: string; slug?: string; position?: number },
): Promise<KbCategoryResponse> {
  return request<KbCategoryResponse>(`/api/kb/categories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteKbCategory(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/kb/categories/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Ticket deflection: up to 5 published articles matching q. */
export function suggestKbArticles(q: string, signal?: AbortSignal): Promise<KbSuggestResponse> {
  return request<KbSuggestResponse>(`/api/kb/suggest${qs({ q })}`, { ...(signal ? { signal } : {}) });
}

// --- KB <-> ticket two-way links (0095) ---
/** Articles linked to a ticket (staff-only). */
export function listTicketKbArticles(ticketId: string, signal?: AbortSignal): Promise<TicketKbArticlesResponse> {
  return request<TicketKbArticlesResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/kb-articles`, {
    ...(signal ? { signal } : {}),
  });
}
/** Link an existing article to a ticket (idempotent). */
export function linkTicketKbArticle(ticketId: string, articleId: string): Promise<TicketKbArticlesResponse> {
  return request<TicketKbArticlesResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/kb-articles`, {
    method: "POST",
    body: { article_id: articleId },
  });
}
/** Remove a link between a ticket and an article. */
export function unlinkTicketKbArticle(ticketId: string, articleId: string): Promise<TicketKbArticlesResponse> {
  return request<TicketKbArticlesResponse>(
    `/api/tickets/${encodeURIComponent(ticketId)}/kb-articles/${encodeURIComponent(articleId)}`,
    { method: "DELETE" },
  );
}
/** Create a DRAFT KB article pre-filled from the ticket, then link it. */
export function createKbArticleFromTicket(
  ticketId: string,
  input: CreateKbFromTicketInput = {},
): Promise<CreateKbFromTicketResponse> {
  return request<CreateKbFromTicketResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/kb-articles/from-ticket`, {
    method: "POST",
    body: input,
  });
}
/** Tickets that reference an article (staff-only). */
export function listArticleTickets(articleId: string, signal?: AbortSignal): Promise<KbLinkedTicketsResponse> {
  return request<KbLinkedTicketsResponse>(`/api/kb/articles/${encodeURIComponent(articleId)}/tickets`, {
    ...(signal ? { signal } : {}),
  });
}

/** KB chat assistant: send the conversation, get a grounded answer + its sources.
 *  Answers only from articles the caller can see (published + their own drafts). */
export function chatKb(messages: KbChatMessage[], chatId?: string, signal?: AbortSignal): Promise<KbChatResponse> {
  return request<KbChatResponse>("/api/kb/chat", {
    method: "POST",
    body: { messages, ...(chatId ? { chat_id: chatId } : {}) },
    ...(signal ? { signal } : {}),
  });
}

/** A user's saved KB chats, newest first. */
export function listKbChats(signal?: AbortSignal): Promise<KbChatsResponse> {
  return request<KbChatsResponse>("/api/kb/chats", { ...(signal ? { signal } : {}) });
}

/** Reopen a saved KB chat with its full transcript. */
export function getKbChat(id: string, signal?: AbortSignal): Promise<KbChatDetailResponse> {
  return request<KbChatDetailResponse>(`/api/kb/chats/${encodeURIComponent(id)}`, { ...(signal ? { signal } : {}) });
}

export function deleteKbChat(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/kb/chats/${encodeURIComponent(id)}`, { method: "DELETE" });
}


// ===== Ticket attachments (raw-bytes upload + authenticated download) ======

/** Download an attachment via authenticated fetch → blob → temp link. A plain
 *  <a href> can't carry the Bearer token (or the staging tenant-host header),
 *  so the bytes are fetched and saved client-side. */
export async function downloadAttachment(id: string, filename: string): Promise<void> {
  const token = getToken() ?? "";
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;
  const res = await fetch(`${API_BASE}/api/attachments/${encodeURIComponent(id)}`, { headers });
  if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`);
  saveBlob(await res.blob(), filename);
}

/** Remove an attachment (deletes the R2 object + the row; parent-guarded). */
export function deleteAttachment(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/attachments/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ===== Manual user management (Settings → Users; user.admin) ===============

export function createUser(input: {
  name: string;
  email: string;
  password?: string;
  /** Any role code defined for the tenant (system or custom). */
  role?: string;
  /** The department to place the user in (0100); null/omit = none. */
  department_id?: string | null;
}): Promise<{ user: TenantUser }> {
  return request<{ user: TenantUser }>("/api/users", { method: "POST", body: input });
}

export function updateUser(
  id: string,
  patch: { name?: string; status?: "active" | "disabled"; manager_id?: string | null; access_override?: 0 | 1 | null; avatar_url?: string | null; department_id?: string | null },
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/users/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

/** Assign any tenant-defined role code (system or custom). */
export function setUserRole(id: string, role: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/users/${encodeURIComponent(id)}/role`, { method: "POST", body: { role } });
}

// ===== Roles & permissions (granular module access; user.admin) =============

export function listRoles(signal?: AbortSignal): Promise<RolesResponse> {
  return request<RolesResponse>("/api/roles", { ...(signal ? { signal } : {}) });
}

export function listPermissions(signal?: AbortSignal): Promise<PermissionsResponse> {
  return request<PermissionsResponse>("/api/permissions", { ...(signal ? { signal } : {}) });
}

/** Role-visibility matrix: which roles appear in each people picker. */
export function getRoleVisibility(signal?: AbortSignal): Promise<RoleVisibilityResponse> {
  return request<RoleVisibilityResponse>("/api/settings/role-visibility", { ...(signal ? { signal } : {}) });
}

export function setRoleVisibility(matrix: Record<string, string[]>): Promise<{ ok: true }> {
  return request<{ ok: true }>("/api/settings/role-visibility", { method: "PUT", body: { matrix } });
}

export function createRole(input: {
  code: string;
  name: string;
  permissions: string[];
}): Promise<{ id: string }> {
  return request<{ id: string }>("/api/roles", { method: "POST", body: input });
}

export function updateRole(
  id: string,
  patch: { name?: string; permissions?: string[]; grants_access?: boolean },
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/roles/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteRole(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/roles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function resetUserPassword(id: string, password: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/users/${encodeURIComponent(id)}/password`, {
    method: "POST",
    body: { password },
  });
}

// ===== Legacy helpdesk parity (locations / categories / checklist / approvals)
// Type-only import kept local so this block stays append-only.
import type {
  ApprovalResponse,
  MyApprovalsResponse,
  TaskApprovalResponse,
  ChecklistItemResponse,
  CreateLocationInput,
  DecideApprovalInput,
  LocationResponse,
  LocationRow,
  LocationsResponse,
  RequestApprovalInput,
  TicketCategoriesResponse,
  TicketCategoryRow,
} from "./types";

// ----- Locations config (GET/POST/PATCH/DELETE /api/config/locations) -----

/** Tolerates either a bare array or the {locations} envelope. */
export async function listLocations(signal?: AbortSignal): Promise<LocationsResponse> {
  const res = await request<LocationsResponse | LocationRow[]>("/api/config/locations", {
    ...(signal ? { signal } : {}),
  });
  return Array.isArray(res) ? { locations: res } : res;
}

export function createLocation(input: CreateLocationInput): Promise<LocationResponse> {
  return request<LocationResponse>("/api/config/locations", { method: "POST", body: input });
}

export function updateLocation(id: string, patch: Partial<CreateLocationInput>): Promise<LocationResponse> {
  return request<LocationResponse>(`/api/config/locations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteLocation(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/locations/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ----- Ticket categories (hierarchical: parent = Request Type, child = subtype) -----

/** Tolerates either a bare array or the {categories} envelope. */
export async function listTicketCategories(signal?: AbortSignal): Promise<TicketCategoriesResponse> {
  const res = await request<TicketCategoriesResponse | TicketCategoryRow[]>(
    "/api/config/ticket-categories",
    { ...(signal ? { signal } : {}) },
  );
  return Array.isArray(res) ? { categories: res } : res;
}

export function createTicketCategory(input: {
  name: string;
  parent_id?: string | null;
  position?: number;
  default_assignee_id?: string | null;
}): Promise<{ category: TicketCategoryRow }> {
  return request<{ category: TicketCategoryRow }>("/api/config/ticket-categories", {
    method: "POST",
    body: input,
  });
}

export function updateTicketCategory(
  id: string,
  patch: { name?: string; parent_id?: string | null; position?: number; default_assignee_id?: string | null },
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/ticket-categories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

// ----- Ticket checklist -----

export function addChecklistItem(ticketId: string, label: string): Promise<ChecklistItemResponse> {
  return request<ChecklistItemResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/checklist`, {
    method: "POST",
    body: { label },
  });
}

export function updateChecklistItem(
  id: string,
  patch: { label?: string; done?: boolean },
): Promise<ChecklistItemResponse> {
  return request<ChecklistItemResponse>(`/api/checklist/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteChecklistItem(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/checklist/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ----- Ticket approvals -----

export function requestTicketApproval(
  ticketId: string,
  input: RequestApprovalInput,
): Promise<ApprovalResponse> {
  return request<ApprovalResponse>(`/api/tickets/${encodeURIComponent(ticketId)}/approvals`, {
    method: "POST",
    body: input,
  });
}

export function decideTicketApproval(id: string, input: DecideApprovalInput): Promise<ApprovalResponse> {
  return request<ApprovalResponse>(`/api/approvals/${encodeURIComponent(id)}/decide`, {
    method: "POST",
    body: input,
  });
}

/** Ticket + task approvals awaiting the signed-in user (Access Requests + bell). */
export function listMyTicketApprovals(signal?: AbortSignal): Promise<MyApprovalsResponse> {
  return request<MyApprovalsResponse>("/api/approvals/mine", { ...(signal ? { signal } : {}) });
}

// ---- Task / action-item approvals (0082) — same module, task endpoints. ----
export function requestTaskApproval(taskId: string, input: RequestApprovalInput): Promise<TaskApprovalResponse> {
  return request<TaskApprovalResponse>(`/api/tasks/${encodeURIComponent(taskId)}/approvals`, {
    method: "POST",
    body: input,
  });
}
export function decideTaskApproval(id: string, input: DecideApprovalInput): Promise<TaskApprovalResponse> {
  return request<TaskApprovalResponse>(`/api/task-approvals/${encodeURIComponent(id)}/decide`, {
    method: "POST",
    body: input,
  });
}

/** Inbox quick-filter counts (All/Mine/Open/Pending), respecting other filters. */
export function getTicketCounts(
  filters: { q?: string; priority?: string; department?: string } = {},
  signal?: AbortSignal,
): Promise<{ counts: { all: number; mine: number; open: number; pending: number; my_group: number } }> {
  return request<{ counts: { all: number; mine: number; open: number; pending: number; my_group: number } }>(
    `/api/tickets/counts${qs({ q: filters.q, priority: filters.priority, department: filters.department })}`,
    { ...(signal ? { signal } : {}) },
  );
}

// ===== Outbound email provider (BYO vendor or platform default) =============

export type EmailProviderKind = "platform" | "mailgun" | "sendgrid" | "postmark" | "resend" | "smtp";

export interface EmailConfig {
  provider: EmailProviderKind;
  from: string | null;
  /** Mailgun sending domain (only meaningful when provider = mailgun). */
  domain: string | null;
  /** Whether an API key is stored — the key itself is never returned. */
  has_key: boolean;
  /** SMTP relay settings (provider = smtp). The password is write-only, exposed
   *  only as has_smtp_password. */
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_secure: boolean;
  smtp_username: string | null;
  has_smtp_password: boolean;
}

export function getEmailConfig(signal?: AbortSignal): Promise<{ email: EmailConfig }> {
  return request<{ email: EmailConfig }>("/api/config/email", { ...(signal ? { signal } : {}) });
}

/** Secrets (api_key, smtp_password) are write-only: include them only when the
 *  admin typed a new one. */
export function setEmailConfig(patch: {
  provider?: EmailProviderKind;
  from?: string | null;
  api_key?: string;
  domain?: string | null;
  smtp_host?: string | null;
  smtp_port?: number | null;
  smtp_secure?: boolean;
  smtp_username?: string | null;
  smtp_password?: string;
}): Promise<{ email: EmailConfig }> {
  return request<{ email: EmailConfig }>("/api/config/email", { method: "PUT", body: patch });
}

export function sendTestEmail(to: string): Promise<{ ok: boolean; emailId: string }> {
  return request<{ ok: boolean; emailId: string }>("/api/config/email/test", {
    method: "POST",
    body: { to },
  });
}

// ===== System email templates (per-tenant overrides over built-in defaults) =====

export interface EmailTemplatePlaceholder {
  key: string;
  description: string;
  sample: string;
}
export interface SystemEmailTemplate {
  code: string;
  label: string;
  description: string;
  group: string;
  subject: string;
  body_html: string;
  body_text: string | null;
  placeholders: EmailTemplatePlaceholder[];
  /** True when the tenant has saved an override (vs. the built-in default). */
  customized: boolean;
}

export function listEmailTemplates(signal?: AbortSignal): Promise<{ templates: SystemEmailTemplate[] }> {
  return request<{ templates: SystemEmailTemplate[] }>("/api/config/email/templates", {
    ...(signal ? { signal } : {}),
  });
}

export function saveEmailTemplate(
  code: string,
  patch: { subject: string; body_html: string; body_text?: string | null },
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/email/templates/${encodeURIComponent(code)}`, {
    method: "PUT",
    body: patch,
  });
}

/** Revert a template to its built-in default (deletes the override). */
export function revertEmailTemplate(code: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/config/email/templates/${encodeURIComponent(code)}`, {
    method: "DELETE",
  });
}

export function testEmailTemplate(code: string, to: string): Promise<{ ok: boolean; emailId: string }> {
  return request<{ ok: boolean; emailId: string }>(
    `/api/config/email/templates/${encodeURIComponent(code)}/test`,
    { method: "POST", body: { to } },
  );
}

// ===== Ticket numbering (display prefix + sequence start + zero-padding) =====

export interface TicketNumberingConfig {
  /** Shown before the number, e.g. "TKT-" (may be empty). */
  prefix: string;
  /** Sequence number the tenant's FIRST ticket starts at (≥ 1). */
  start: number;
  /** Minimum digits; the number is zero-padded to this width (0 = no padding). */
  padding: number;
}

export function getTicketNumbering(signal?: AbortSignal): Promise<{ numbering: TicketNumberingConfig }> {
  return request<{ numbering: TicketNumberingConfig }>("/api/config/ticket-numbering", {
    ...(signal ? { signal } : {}),
  });
}

export function setTicketNumbering(patch: Partial<TicketNumberingConfig>): Promise<{ numbering: TicketNumberingConfig }> {
  return request<{ numbering: TicketNumberingConfig }>("/api/config/ticket-numbering", {
    method: "PUT",
    body: patch,
  });
}

// ===== Facility (work order) numbering — mirrors ticket numbering exactly =====

export interface FacilityNumberingConfig {
  /** Shown before the number, e.g. "FAC-" (may be empty). */
  prefix: string;
  /** Sequence number the tenant's FIRST work order starts at (≥ 1). */
  start: number;
  /** Minimum digits; the number is zero-padded to this width (0 = no padding). */
  padding: number;
}

export function getFacilityNumbering(signal?: AbortSignal): Promise<{ numbering: FacilityNumberingConfig }> {
  return request<{ numbering: FacilityNumberingConfig }>("/api/config/facility-numbering", {
    ...(signal ? { signal } : {}),
  });
}

export function setFacilityNumbering(patch: Partial<FacilityNumberingConfig>): Promise<{ numbering: FacilityNumberingConfig }> {
  return request<{ numbering: FacilityNumberingConfig }>("/api/config/facility-numbering", {
    method: "PUT",
    body: patch,
  });
}

// ===== Change-request numbering — mirrors ticket numbering exactly ============

export interface ChangeNumberingConfig {
  /** Shown before the number, e.g. "CHG-" (may be empty). */
  prefix: string;
  /** Sequence number the tenant's FIRST change request starts at (≥ 1). */
  start: number;
  /** Minimum digits; the number is zero-padded to this width (0 = no padding). */
  padding: number;
}

export function getChangeNumbering(signal?: AbortSignal): Promise<{ numbering: ChangeNumberingConfig }> {
  return request<{ numbering: ChangeNumberingConfig }>("/api/config/change-numbering", {
    ...(signal ? { signal } : {}),
  });
}

export function setChangeNumbering(patch: Partial<ChangeNumberingConfig>): Promise<{ numbering: ChangeNumberingConfig }> {
  return request<{ numbering: ChangeNumberingConfig }>("/api/config/change-numbering", {
    method: "PUT",
    body: patch,
  });
}

// ===== Facility value sets (configurable statuses / categories / wo types) ====

/** One option in a configurable facility list. `code` is the stable identifier
 *  stored on work-order rows; `label` is display-only. */
export interface FacilityOption {
  code: string;
  label: string;
  color?: string | null;
}
/** A building inside a campus (cascade level 2). */
export interface FacilityBuildingOption extends FacilityOption {
  campus_code: string;
}
/** A room inside a building (cascade level 3). */
export interface FacilityRoomOption extends FacilityOption {
  building_code: string;
}
export interface FacilityConfig {
  statuses: FacilityOption[];
  categories: FacilityOption[];
  wo_types: FacilityOption[];
  priorities: FacilityOption[];
  /** Campus → Building → Room cascade, shared by work orders + tickets. */
  campuses: FacilityOption[];
  buildings: FacilityBuildingOption[];
  rooms: FacilityRoomOption[];
}
/** The shared place cascade (campuses + buildings + rooms), used by the picker. */
export interface PlacesConfig {
  campuses: FacilityOption[];
  buildings: FacilityBuildingOption[];
  rooms: FacilityRoomOption[];
}

export function getFacilityConfig(signal?: AbortSignal): Promise<{ config: FacilityConfig }> {
  return request<{ config: FacilityConfig }>("/api/config/facility-config", {
    ...(signal ? { signal } : {}),
  });
}

/** Replace whole lists (only those provided). Codes are re-slugified server-side
 *  from the label when omitted; an empty array reverts a list to its default. */
export function setFacilityConfig(patch: Partial<FacilityConfig>): Promise<{ config: FacilityConfig }> {
  return request<{ config: FacilityConfig }>("/api/config/facility-config", {
    method: "PUT",
    body: patch,
  });
}

/** Shared Campus → Building → Room cascade, readable by ANY authenticated member
 *  (work orders + tickets both use it). Lighter than getFacilityConfig, which
 *  needs facility.read. */
export function getPlaces(signal?: AbortSignal): Promise<PlacesConfig> {
  return request<PlacesConfig>("/api/config/places", { ...(signal ? { signal } : {}) });
}

// ===== Facilities module (building-management request queue) ===============
// Mirrors the `facility-*` routes in workers/ac-edge/src/index.ts. Gated by the
// `facility.*` permissions server-side (read for GETs, write for mutations,
// admin for delete).

// ===== Change Management (0091) — operational/IT change requests ===========
// A first-class work type (stable id "change_management"). change.read to view,
// change.write to create/edit/comment, change.admin to delete (server-enforced).
export function listChangeRequests(
  filter: ChangeRequestFilter = {},
  signal?: AbortSignal,
): Promise<ChangeRequestsResponse> {
  return request<ChangeRequestsResponse>(
    `/api/change-requests${qs({
      q: filter.q,
      status: filter.status,
      risk: filter.risk,
      change_type: filter.change_type,
      assignee: filter.assignee,
      department: filter.department_id,
      urgency: filter.urgency,
      business_impact: filter.business_impact,
    })}`,
    { ...(signal ? { signal } : {}) },
  );
}

export function getChangeRequest(id: string, signal?: AbortSignal): Promise<ChangeRequestDetailResponse> {
  return request<ChangeRequestDetailResponse>(`/api/change-requests/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function createChangeRequest(body: CreateChangeRequestInput): Promise<ChangeRequestResponse> {
  return request<ChangeRequestResponse>("/api/change-requests", { method: "POST", body });
}

export function updateChangeRequest(id: string, patch: UpdateChangeRequestInput): Promise<ChangeRequestResponse> {
  return request<ChangeRequestResponse>(`/api/change-requests/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteChangeRequest(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/change-requests/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function addChangeRequestComment(id: string, body: string): Promise<ChangeRequestCommentResponse> {
  return request<ChangeRequestCommentResponse>(`/api/change-requests/${encodeURIComponent(id)}/comments`, {
    method: "POST",
    body: { body },
  });
}

export function editChangeRequestComment(id: string, commentId: string, body: string): Promise<ChangeRequestCommentResponse> {
  return request<ChangeRequestCommentResponse>(
    `/api/change-requests/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
    { method: "PATCH", body: { body } },
  );
}

export function deleteChangeRequestComment(id: string, commentId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/change-requests/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE" },
  );
}

// ----- Change approvers (0098) — multiple approvers per change request. -----
export function listChangeApprovers(id: string, signal?: AbortSignal): Promise<ChangeApproversResponse> {
  return request<ChangeApproversResponse>(`/api/change-requests/${encodeURIComponent(id)}/approvers`, {
    ...(signal ? { signal } : {}),
  });
}
export function setChangeApprovers(id: string, userIds: string[]): Promise<ChangeApproversResponse> {
  return request<ChangeApproversResponse>(`/api/change-requests/${encodeURIComponent(id)}/approvers`, {
    method: "PUT",
    body: { user_ids: userIds },
  });
}
/** Record the signed-in user's decision on a change they're an approver on. */
export function decideChangeApproval(
  id: string,
  input: { decision: "approved" | "rejected"; note?: string },
): Promise<{ approval: ChangeApprover }> {
  return request<{ approval: ChangeApprover }>(`/api/change-requests/${encodeURIComponent(id)}/approvers/decide`, {
    method: "POST",
    body: input,
  });
}

// ----- Change-Management configurable value sets (Settings → Change Mgmt). ----
export type { ChangeConfig, ChangeOption } from "./types";
export function getChangeConfig(signal?: AbortSignal): Promise<{ config: ChangeConfig }> {
  return request<{ config: ChangeConfig }>("/api/config/change", { ...(signal ? { signal } : {}) });
}
export function setChangeConfig(patch: Partial<ChangeConfig>): Promise<{ config: ChangeConfig }> {
  return request<{ config: ChangeConfig }>("/api/config/change", { method: "PUT", body: patch });
}

export function listFacilityRequests(
  filter: FacilityRequestFilter = {},
  signal?: AbortSignal,
): Promise<FacilityRequestsResponse> {
  return request<FacilityRequestsResponse>(
    `/api/facility-requests${qs({
      q: filter.q,
      status: filter.status,
      priority: filter.priority,
      category: filter.category,
      assignee: filter.assignee,
      building: filter.building,
    })}`,
    { ...(signal ? { signal } : {}) },
  );
}

export function getFacilityRequest(
  id: string,
  signal?: AbortSignal,
): Promise<FacilityRequestDetailResponse> {
  return request<FacilityRequestDetailResponse>(
    `/api/facility-requests/${encodeURIComponent(id)}`,
    { ...(signal ? { signal } : {}) },
  );
}

export function createFacilityRequest(
  body: CreateFacilityRequestInput,
): Promise<FacilityRequestResponse> {
  return request<FacilityRequestResponse>("/api/facility-requests", { method: "POST", body });
}

// ===== Facility groups (0084) — campus/team-scoped visibility =============
export function listFacilityGroups(signal?: AbortSignal): Promise<FacilityGroupsResponse> {
  return request<FacilityGroupsResponse>("/api/facility/groups", { ...(signal ? { signal } : {}) });
}
export function createFacilityGroup(body: { name: string; description?: string | null }): Promise<{ group: FacilityGroupRow }> {
  return request<{ group: FacilityGroupRow }>("/api/facility/groups", { method: "POST", body });
}
export function updateFacilityGroup(id: string, patch: { name?: string; description?: string | null }): Promise<{ group: FacilityGroupRow }> {
  return request<{ group: FacilityGroupRow }>(`/api/facility/groups/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}
export function deleteFacilityGroup(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/facility/groups/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function listFacilityGroupMembers(id: string, signal?: AbortSignal): Promise<FacilityGroupMembersResponse> {
  return request<FacilityGroupMembersResponse>(`/api/facility/groups/${encodeURIComponent(id)}/members`, { ...(signal ? { signal } : {}) });
}
export function addFacilityGroupMember(id: string, body: { user_id: string; role?: "member" | "lead" }): Promise<FacilityGroupMembersResponse> {
  return request<FacilityGroupMembersResponse>(`/api/facility/groups/${encodeURIComponent(id)}/members`, { method: "POST", body });
}
export function removeFacilityGroupMember(id: string, userId: string): Promise<FacilityGroupMembersResponse> {
  return request<FacilityGroupMembersResponse>(`/api/facility/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

// ===== Facility group team chat (0087) ====================================
/** A group's chat thread (oldest-first). Members + admins only (gated server-side). */
export function listFacilityGroupMessages(id: string, signal?: AbortSignal): Promise<FacilityGroupMessagesResponse> {
  return request<FacilityGroupMessagesResponse>(`/api/facility/groups/${encodeURIComponent(id)}/messages`, { ...(signal ? { signal } : {}) });
}
export function postFacilityGroupMessage(id: string, body: string): Promise<{ message: FacilityGroupMessageRow }> {
  return request<{ message: FacilityGroupMessageRow }>(`/api/facility/groups/${encodeURIComponent(id)}/messages`, { method: "POST", body: { body } });
}

// Vendors & suppliers (0085). read to list, write to create/update, admin to
// delete (enforced server-side). Asset↔vendor links are a full-replace PUT.
export function listFacilityVendors(signal?: AbortSignal): Promise<FacilityVendorsResponse> {
  return request<FacilityVendorsResponse>("/api/facility/vendors", { ...(signal ? { signal } : {}) });
}
export function createFacilityVendor(body: CreateFacilityVendorInput): Promise<FacilityVendorResponse> {
  return request<FacilityVendorResponse>("/api/facility/vendors", { method: "POST", body });
}
export function updateFacilityVendor(id: string, patch: UpdateFacilityVendorInput): Promise<FacilityVendorResponse> {
  return request<FacilityVendorResponse>(`/api/facility/vendors/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}
export function deleteFacilityVendor(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/facility/vendors/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function listAssetVendors(assetId: string, signal?: AbortSignal): Promise<FacilityAssetVendorsResponse> {
  return request<FacilityAssetVendorsResponse>(`/api/facility-assets/${encodeURIComponent(assetId)}/vendors`, { ...(signal ? { signal } : {}) });
}
export function setAssetVendors(
  assetId: string,
  vendors: { vendor_id: string; is_primary?: boolean; note?: string | null }[],
): Promise<FacilityAssetVendorsResponse> {
  return request<FacilityAssetVendorsResponse>(`/api/facility-assets/${encodeURIComponent(assetId)}/vendors`, { method: "PUT", body: { vendors } });
}

export function updateFacilityRequest(
  id: string,
  patch: UpdateFacilityRequestInput,
): Promise<FacilityRequestResponse> {
  return request<FacilityRequestResponse>(`/api/facility-requests/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteFacilityRequest(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/facility-requests/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function addFacilityComment(
  id: string,
  body: string,
  opts?: { notify_requester?: boolean; notify_assignee?: boolean; is_internal?: boolean },
): Promise<FacilityCommentResponse> {
  return request<FacilityCommentResponse>(
    `/api/facility-requests/${encodeURIComponent(id)}/comments`,
    { method: "POST", body: { body, ...(opts ?? {}) } },
  );
}

/** Toggle an emoji reaction on a work-order comment (0087); returns the refreshed
 *  comment thread. Mirrors toggleTaskCommentReaction. */
export function toggleFacilityCommentReaction(
  requestId: string,
  commentId: string,
  emoji: string,
): Promise<{ comments: FacilityRequestComment[] }> {
  return request<{ comments: FacilityRequestComment[] }>(
    `/api/facility-requests/${encodeURIComponent(requestId)}/comments/${encodeURIComponent(commentId)}/react`,
    { method: "POST", body: { emoji } },
  );
}

/** Upload a file to a facility request (raw bytes + x-filename, like the ticket
 *  attachment upload). */
export async function uploadFacilityAttachment(
  id: string,
  file: File,
): Promise<{ attachment: AttachmentRow }> {
  const token = getToken() ?? "";
  const headers: Record<string, string> = {
    "x-filename": file.name.replace(/[^\x20-\x7E]/g, "_") || "upload.bin",
    "Content-Type": file.type || "application/octet-stream",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;
  const res = await fetch(
    `${API_BASE}/api/facility-requests/${encodeURIComponent(id)}/attachments`,
    { method: "POST", headers, body: await file.arrayBuffer() },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || `Upload failed (${res.status})`);
  }
  return (await res.json()) as { attachment: AttachmentRow };
}

export function deleteFacilityAttachment(id: string, attId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/facility-requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`,
    { method: "DELETE" },
  );
}

export async function uploadChangeAttachment(id: string, file: File): Promise<{ attachment: AttachmentRow }> {
  const token = getToken() ?? "";
  const headers: Record<string, string> = {
    "x-filename": file.name.replace(/[^\x20-\x7E]/g, "_") || "upload.bin",
    "Content-Type": file.type || "application/octet-stream",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;
  const res = await fetch(`${API_BASE}/api/change-requests/${encodeURIComponent(id)}/attachments`, {
    method: "POST",
    headers,
    body: await file.arrayBuffer(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || `Upload failed (${res.status})`);
  }
  return (await res.json()) as { attachment: AttachmentRow };
}

export function deleteChangeAttachment(id: string, attId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/change-requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`,
    { method: "DELETE" },
  );
}

/** Upload a file to a task / action item (raw bytes + x-filename, like the
 *  ticket attachment upload). Action items are tasks, so this covers both. */
export async function uploadTaskAttachment(
  taskId: string,
  file: File,
): Promise<{ attachment: AttachmentRow }> {
  const token = getToken() ?? "";
  const headers: Record<string, string> = {
    "x-filename": file.name.replace(/[^\x20-\x7E]/g, "_") || "upload.bin",
    "Content-Type": file.type || "application/octet-stream",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;
  const res = await fetch(
    `${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/attachments`,
    { method: "POST", headers, body: await file.arrayBuffer() },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || `Upload failed (${res.status})`);
  }
  return (await res.json()) as { attachment: AttachmentRow };
}

export function deleteTaskAttachment(taskId: string, attId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/tasks/${encodeURIComponent(taskId)}/attachments/${encodeURIComponent(attId)}`,
    { method: "DELETE" },
  );
}

// Facility assets — the equipment registry (0039). Read for GETs, write for
// create/update, admin for delete (enforced server-side).

export function listFacilityAssets(
  filter: FacilityAssetFilter = {},
  signal?: AbortSignal,
): Promise<FacilityAssetsResponse> {
  return request<FacilityAssetsResponse>(
    `/api/facility-assets${qs({
      q: filter.q,
      status: filter.status,
      building: filter.building,
      category: filter.category,
    })}`,
    { ...(signal ? { signal } : {}) },
  );
}

export function getFacilityAsset(
  id: string,
  signal?: AbortSignal,
): Promise<FacilityAssetDetailResponse> {
  return request<FacilityAssetDetailResponse>(`/api/facility-assets/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function createFacilityAsset(body: CreateFacilityAssetInput): Promise<FacilityAssetResponse> {
  return request<FacilityAssetResponse>("/api/facility-assets", { method: "POST", body });
}

export function updateFacilityAsset(
  id: string,
  patch: UpdateFacilityAssetInput,
): Promise<FacilityAssetResponse> {
  return request<FacilityAssetResponse>(`/api/facility-assets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteFacilityAsset(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/facility-assets/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// 0070: asset maintenance history (work orders) + downtime tracking.

export function getFacilityAssetWorkOrders(
  id: string,
  signal?: AbortSignal,
): Promise<FacilityAssetWorkOrdersResponse> {
  return request<FacilityAssetWorkOrdersResponse>(
    `/api/facility-assets/${encodeURIComponent(id)}/work-orders`,
    { ...(signal ? { signal } : {}) },
  );
}

export function getFacilityAssetDowntime(
  id: string,
  signal?: AbortSignal,
): Promise<FacilityAssetDowntimeResponse> {
  return request<FacilityAssetDowntimeResponse>(
    `/api/facility-assets/${encodeURIComponent(id)}/downtime`,
    { ...(signal ? { signal } : {}) },
  );
}

export function startFacilityAssetDowntime(
  id: string,
  body: { reason?: string | null; work_order_id?: string | null } = {},
): Promise<FacilityDowntimeMutationResponse> {
  return request<FacilityDowntimeMutationResponse>(
    `/api/facility-assets/${encodeURIComponent(id)}/downtime`,
    { method: "POST", body },
  );
}

export function stopFacilityAssetDowntime(
  id: string,
): Promise<FacilityDowntimeMutationResponse> {
  return request<FacilityDowntimeMutationResponse>(
    `/api/facility-assets/${encodeURIComponent(id)}/downtime/stop`,
    { method: "POST", body: {} },
  );
}

// Facility request checklist / procedures (0039). Read for the list, write for
// add / toggle / delete.

export function listFacilityChecklist(
  reqId: string,
  signal?: AbortSignal,
): Promise<FacilityChecklistResponse> {
  return request<FacilityChecklistResponse>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/checklist`,
    { ...(signal ? { signal } : {}) },
  );
}

export function addFacilityChecklistItem(
  reqId: string,
  label: string,
): Promise<FacilityChecklistItemResponse> {
  return request<FacilityChecklistItemResponse>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/checklist`,
    { method: "POST", body: { label } },
  );
}

export function toggleFacilityChecklistItem(
  reqId: string,
  itemId: string,
  isDone: boolean,
): Promise<FacilityChecklistItemResponse> {
  return request<FacilityChecklistItemResponse>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/checklist/${encodeURIComponent(itemId)}`,
    { method: "PATCH", body: { is_done: isDone } },
  );
}

export function deleteFacilityChecklistItem(
  reqId: string,
  itemId: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/checklist/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
  );
}

// Per-work-order to-do tasks (0086). Read to list, write to add/update/delete.
// listMyFacilityTasks is the cross-WO "my tasks" feed; ?due=today filters to
// tasks due today-or-overdue.

export function listRequestTasks(
  reqId: string,
  signal?: AbortSignal,
): Promise<FacilityTasksResponse> {
  return request<FacilityTasksResponse>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/tasks`,
    { ...(signal ? { signal } : {}) },
  );
}

export function addRequestTask(
  reqId: string,
  input: { title: string; assignee_id?: string | null; due_date?: number | null },
): Promise<FacilityTaskResponse> {
  return request<FacilityTaskResponse>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/tasks`,
    { method: "POST", body: input },
  );
}

export function updateRequestTask(
  taskId: string,
  patch: {
    title?: string;
    assignee_id?: string | null;
    due_date?: number | null;
    status?: string;
    position?: number;
  },
): Promise<FacilityTaskResponse> {
  return request<FacilityTaskResponse>(
    `/api/facility-tasks/${encodeURIComponent(taskId)}`,
    { method: "PATCH", body: patch },
  );
}

export function deleteRequestTask(taskId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/facility-tasks/${encodeURIComponent(taskId)}`,
    { method: "DELETE" },
  );
}

export function listMyFacilityTasks(
  opts: { dueToday?: boolean } = {},
  signal?: AbortSignal,
): Promise<FacilityTasksResponse> {
  const qs = opts.dueToday ? "?due=today" : "";
  return request<FacilityTasksResponse>(`/api/facility/my-tasks${qs}`, {
    ...(signal ? { signal } : {}),
  });
}

// Facility parts / inventory catalog (0040). Read for GETs, write for
// create/update, admin for delete (enforced server-side).

export function listFacilityParts(
  filter: FacilityPartFilter = {},
  signal?: AbortSignal,
): Promise<FacilityPartsResponse> {
  return request<FacilityPartsResponse>(
    `/api/facility-parts${qs({
      q: filter.q,
      category: filter.category,
      lowStock: filter.lowStock ? "1" : undefined,
      belowReorder: filter.belowReorder ? "1" : undefined,
    })}`,
    { ...(signal ? { signal } : {}) },
  );
}

/** 0072: parts that have crossed their reorder_point (the low-stock alert set). */
export function listLowStockFacilityParts(
  signal?: AbortSignal,
): Promise<FacilityPartsResponse> {
  return request<FacilityPartsResponse>("/api/facility-parts/low-stock", {
    ...(signal ? { signal } : {}),
  });
}

export function getFacilityPart(id: string, signal?: AbortSignal): Promise<FacilityPartResponse> {
  return request<FacilityPartResponse>(`/api/facility-parts/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function createFacilityPart(body: CreateFacilityPartInput): Promise<FacilityPartResponse> {
  return request<FacilityPartResponse>("/api/facility-parts", { method: "POST", body });
}

export function updateFacilityPart(
  id: string,
  patch: UpdateFacilityPartInput,
): Promise<FacilityPartResponse> {
  return request<FacilityPartResponse>(`/api/facility-parts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteFacilityPart(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/facility-parts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// Work-order parts — parts consumed on a facility request (0040). Adding a part
// decrements catalog stock; removing it restores stock (enforced server-side).

export function listRequestParts(
  reqId: string,
  signal?: AbortSignal,
): Promise<FacilityRequestPartsResponse> {
  return request<FacilityRequestPartsResponse>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/parts`,
    { ...(signal ? { signal } : {}) },
  );
}

export function addPartToRequest(
  reqId: string,
  body: { part_id: string; quantity: number },
): Promise<FacilityRequestPartResponse> {
  return request<FacilityRequestPartResponse>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/parts`,
    { method: "POST", body },
  );
}

export function removeRequestPart(reqId: string, linkId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/facility-requests/${encodeURIComponent(reqId)}/parts/${encodeURIComponent(linkId)}`,
    { method: "DELETE" },
  );
}

// Preventive-maintenance schedule templates (0071). Read to list, write to
// create/update + generate, admin to delete (enforced server-side).

export function listPmTemplates(signal?: AbortSignal): Promise<FacilityPmTemplatesResponse> {
  return request<FacilityPmTemplatesResponse>("/api/facility/pm/templates", {
    ...(signal ? { signal } : {}),
  });
}

export function createPmTemplate(
  body: CreateFacilityPmTemplateInput,
): Promise<FacilityPmTemplateResponse> {
  return request<FacilityPmTemplateResponse>("/api/facility/pm/templates", {
    method: "POST",
    body,
  });
}

export function updatePmTemplate(
  id: string,
  patch: UpdateFacilityPmTemplateInput,
): Promise<FacilityPmTemplateResponse> {
  return request<FacilityPmTemplateResponse>(
    `/api/facility/pm/templates/${encodeURIComponent(id)}`,
    { method: "PATCH", body: patch },
  );
}

export function deletePmTemplate(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/facility/pm/templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function generateDuePms(): Promise<FacilityPmGenerateResponse> {
  return request<FacilityPmGenerateResponse>("/api/facility/pm/generate", { method: "POST" });
}
